import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { useNavigate, useLocation } from "react-router-dom";
import { API_BASE_URL } from "../../config";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import { Progress } from "../../components/ui/progress";
import { toast } from "sonner";
import { marked } from "marked";
import { Label } from "../../components/ui/label";
import { useDropzone } from "react-dropzone";
import {
    FileText,
    Plus,
    Trash2,
    ChevronRight,
    ChevronLeft,
    Download,
    Shield,
    Sparkles,
    CheckCircle2,
    Loader2,
    ScrollText,
    Users,
    Settings2,
    ArrowRight,
    Copy,
    ExternalLink,
    Scale,
    Building2,
    Globe,
    Gauge,
    FileDown,
    RotateCcw,
    Eye,
    UploadCloud,
    AlertCircle,
    AlertTriangle,
    ShieldAlert,
    Info,
    LayoutDashboard,
    History,
    Clock,
    Search as SearchIcon,
    Filter,
    Gavel,
    FolderOpen,
    FileCheck,
    X,
    BookOpen,
    MessageSquare,
    Check,
    XCircle,
    Send,
    ChevronDown,
    ChevronUp,
    RefreshCw,
    GripVertical,
    BarChart3,
    Activity
} from "lucide-react";
import {
    Radar,
    RadarChart,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
    ResponsiveContainer,
    Tooltip as RechartsTooltip
} from 'recharts';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from "../../components/ui/sheet";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
} from "../../components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { useApiClient } from "@/hooks/use-api-client";
import { useAgentExecution } from "@/contexts/AgentExecutionContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/lib/supabase";
import { UsageTracker } from "@/lib/services/usage-tracker";
import LegalEditor from "../../components/editor/LegalEditor";
import { useDocumentViewer } from "@/contexts/DocumentViewerContext";
import { useDespachos } from "@/hooks/useDespachos";
import { fetchScrapperProcesos, getProcesoFiles, ScrapperProceso, ProcesoArchivo } from "@/services/scraperApi";
import { getAgentBySlug } from "@/lib/agents";

// ─── Types ───────────────────────────────────────────────────────────────────

interface AnalysisResult {
    status?: "processing" | "completed" | "failed" | "rejected";
    error?: string;
    message?: string;
    guard_rail_details?: {
        is_valid: boolean;
        confidence: number;
        reason: string;
    };
    summary_bluf?: string;
    index_contractual_risk?: "Bajo" | "Medio" | "Alto";
    risk_score?: number;
    risk_heatmap?: Record<string, number>;
    confidence_indicator?: {
        score: number;
        justification: string[];
    };
    risk_analysis?: Array<{
        risk_level: "Alto" | "Medio" | "Bajo";
        clause_name: string;
        original_text: string;
        problem_detected: string;
        legal_basis: string;
        recommended_action?: string;
        suggested_redline: string;
        benchmark?: string;
        playbook_link?: string;
    }>;
    extracted_text?: string;
    negotiation_strategy?: {
        counterparty_position: string;
        critical_clauses: string[];
        recommendations: string[];
    };
    compliance_check?: {
        overall_status: string;
        regulations: Array<{
            norma: string;
            status: string;
        }>;
    };
    explainability?: {
        methodology: string[];
    };
    // Legacy support
    summary?: string;
    risk_level?: string;
    risks?: string[];
    obligations?: Array<{
        parte: string;
        obligaciones: string[];
    }>;
    recommendations?: string[];
    original_file_name?: string;
    summary_pdf_url?: string;
    document_id?: string;
    timestamp?: string;
    playbook_review?: PlaybookReviewResult | null;
    db_id?: string;
    id?: string;
}

interface HistoryItem extends AnalysisResult {
    id: string;
    created_at: string;
    contract_type?: string;
    storage_file_name?: string;
    contract_url?: string;
}

interface ReviewOptions {
    riskFinancial: boolean;
    riskLegal: boolean;
    riskCompliance: boolean;
    clauseImportant: boolean;
    clauseObligations: boolean;
    clauseTermination: boolean;
}

// ─── Playbook / Legal Agent Types ────────────────────────────────────────────

interface PlaybookTopicRule {
    text: string;
    source_reference?: string;
}

interface PlaybookTopicExampleClause {
    text: string;
    label?: string;
}

interface PlaybookTopic {
    id: string;
    name: string;
    description?: string;
    rules: PlaybookTopicRule[];
    example_clauses: PlaybookTopicExampleClause[];
    legal_framework?: string;
}

interface PlaybookSkill {
    id: string;
    name: string;
    description?: string;
    source_filename?: string;
    topics: PlaybookTopic[];
    created_at?: string;
    updated_at?: string;
}

interface TopicComplianceResult {
    topic_id: string;
    topic_name: string;
    status: 'gray' | 'green' | 'light-red' | 'dark-red';
    analysis: string;
    rules_evaluated: Array<{ rule: string; compliant: string; detail: string }>;
    citations: Array<{ text: string; location: string }>;
    suggested_edit?: { original: string; replacement: string; comment?: string };
}

interface PlaybookReviewResult {
    skill_name: string;
    overall_status: string;
    summary: string;
    topics: TopicComplianceResult[];
}

interface ChatMessage {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    citations?: Array<{ text: string; index: number }>;
}

// ─── Premium Styles ──────────────────────────────────────────────────────────

function PremiumStyles() {
    return (
        <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

      .crv2-root {
        font-family: 'Inter', system-ui, -apple-system, sans-serif;
        background: linear-gradient(135deg, #f8fafc 0%, #eef2ff 50%, #f1f5f9 100%);
        height: 100vh;
        display: flex;
        flex-direction: column;
        overflow: hidden;
      }

      /* ─── Header ─── */
      .crv2-header {
        background: rgba(255, 255, 255, 0.85);
        backdrop-filter: blur(20px);
        padding: 0.75rem 2rem;
        position: sticky;
        top: 0;
        z-index: 50;
        border-bottom: 1px solid rgba(226, 232, 240, 0.8);
        border-top: 3px solid #2563eb;
        transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        box-shadow: 0 4px 25px -5px rgba(0, 0, 0, 0.05);
      }
      .crv2-header::before {
        content: '';
        position: absolute;
        inset: 0;
        background: linear-gradient(to bottom right, rgba(59, 130, 246, 0.05), rgba(99, 102, 241, 0.05));
        pointer-events: none;
      }
      .crv2-header-content {
        position: relative;
        z-index: 2;
        max-width: 1600px;
        margin: 0 auto;
      }

      /* ─── Compact Stepper ─── */
      .crv2-stepper-container {
        padding: 0.75rem 0 0.25rem;
        margin-top: 0.5rem;
        border-top: 1px solid rgba(226, 232, 240, 0.5);
      }
      .crv2-stepper {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 2.5rem;
      }
      .crv2-step {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        color: #64748b;
        font-size: 11px;
        font-weight: 700;
        transition: all 0.4s ease;
        opacity: 0.6;
        letter-spacing: 0.02em;
        text-transform: uppercase;
      }
      .crv2-step.active {
        color: #0f172a;
        opacity: 0.9;
      }
      .crv2-step.current {
        color: #2563eb;
        opacity: 1;
        transform: translateY(-1px);
      }
      .crv2-step-icon {
        width: 28px;
        height: 28px;
        border-radius: 8px;
        background: #f1f5f9;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        border: 1px solid #e2e8f0;
        color: #94a3b8;
      }
      .active .crv2-step-icon {
        background: #eff6ff;
        color: #3b82f6;
        border-color: #bfdbfe;
        box-shadow: 0 2px 10px rgba(59, 130, 246, 0.05);
      }
      .current .crv2-step-icon {
        background: #2563eb;
        color: #fff;
        border-color: #2563eb;
        box-shadow: 0 4px 15px rgba(37, 99, 235, 0.25);
      }
      .crv2-step-connector {
        flex: 1;
        height: 2px;
        background: #e2e8f0;
        max-width: 32px;
        border-radius: 99px;
        transition: all 0.4s ease;
      }
      .crv2-step-connector.active {
        background: #3b82f6;
        box-shadow: 0 0 8px rgba(59, 130, 246, 0.2);
      }

      /* ─── Split Layout ─── */
      .crv2-body {
        display: flex;
        gap: 0;
        flex: 1;
        overflow: hidden;
      }
      .crv2-form-panel {
        width: 33.33%;
        min-width: 380px;
        background: #ffffff;
        border-right: 1px solid #e2e8f0;
        overflow-y: auto;
        padding: 1.5rem 1.25rem;
        box-shadow: 4px 0 24px rgba(0,0,0,0.02);
        z-index: 10;
        transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      }
      .crv2-form-panel.collapsed {
        width: 0;
        min-width: 0;
        padding-left: 0;
        padding-right: 0;
        opacity: 0;
        border-right: 0;
        pointer-events: none;
      }
      .crv2-preview-panel {
        flex: 1;
        background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
        overflow-y: auto;
        padding: 3rem 4rem;
        scroll-behavior: smooth;
        transition: padding 0.4s ease;
      }
      .crv2-preview-panel.step-4 {
        padding: 1.5rem 2rem;
      }
 
      /* â”€â”€â”€ Form Cards â”€â”€â”€ */
      .crv2-field-group {
        background: #fff;
        border: 1px solid #e2e8f0;
        border-radius: 1rem;
        padding: 1.25rem;
        margin-bottom: 1rem;
        transition: all 0.2s;
      }
      .crv2-field-group:hover {
        border-color: #cbd5e1;
        box-shadow: 0 2px 12px rgba(0,0,0,0.03);
      }
      .crv2-field-label {
        font-size: 0.8rem;
        font-weight: 700;
        color: #0f172a;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        margin-bottom: 0.75rem;
        display: flex;
        align-items: center;
        gap: 0.4rem;
      }
 
      /* â”€â”€â”€ Dropzone â”€â”€â”€ */
      .crv2-dropzone {
        border: 2px dashed #e2e8f0;
        border-radius: 1rem;
        padding: 1.5rem 1rem;
        text-align: center;
        transition: all 0.2s;
        cursor: pointer;
        background: #fcfdfe;
      }
      .crv2-dropzone:hover {
        border-color: #94a3b8;
        background: #f8fafc;
      }
      .crv2-dropzone.active {
        border-color: #475569;
        background: #f1f5f9;
      }

      /* ─── Result Cards ─── */
      .crv2-result-card {
        background: #fff;
        border: 1px solid #e2e8f0;
        border-radius: 1rem;
        padding: 1.5rem;
        margin-bottom: 1rem;
        box-shadow: 0 1px 3px rgba(0,0,0,0.05);
      }
      .crv2-risk-badge {
        padding: 0.25rem 0.75rem;
        border-radius: 999px;
        font-size: 0.7rem;
        font-weight: 700;
        text-transform: uppercase;
      }
      .crv2-risk-alto { background: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }
      .crv2-risk-medio { background: #fffbeb; color: #d97706; border: 1px solid #fef3c7; }
      .crv2-risk-bajo { background: #f0fdf4; color: #16a34a; border: 1px solid #dcfce7; }

      /* ─── Welcome State ─── */
      .crv2-welcome {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 100%;
        min-height: 400px;
        text-align: center;
        padding: 3rem;
      }
      .crv2-welcome-icon {
        width: 80px;
        height: 80px;
        border-radius: 1.5rem;
        background: linear-gradient(135deg, #1e293b, #334155, #475569);
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 1.5rem;
        box-shadow: 0 8px 32px rgba(15,23,42,0.15);
        animation: float 3s ease-in-out infinite;
      }
      @keyframes float {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-8px); }
      }

      /* ─── Playbook Compliance Topic Cards ─── */
      .crv2-topic-card {
        border-radius: 1rem;
        padding: 1.25rem;
        margin-bottom: 0.75rem;
        cursor: pointer;
        transition: all 0.2s ease;
        border: 1px solid;
      }
      .crv2-topic-card:hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 20px rgba(0,0,0,0.06);
      }
      .crv2-topic-gray { background: #f8fafc; border-color: #e2e8f0; color: #94a3b8; }
      .crv2-topic-green { background: #f0fdf4; border-color: #bbf7d0; color: #166534; }
      .crv2-topic-light-red { background: #fff7ed; border-color: #fed7aa; color: #9a3412; }
      .crv2-topic-dark-red { background: #fef2f2; border-color: #fecaca; color: #991b1b; }
      .crv2-topic-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
      .crv2-topic-dot.gray { background: #94a3b8; }
      .crv2-topic-dot.green { background: #22c55e; }
      .crv2-topic-dot.light-red { background: #f97316; }
      .crv2-topic-dot.dark-red { background: #ef4444; }

      /* ─── Accept/Dismiss States ─── */
      .crv2-edit-accepted {
        opacity: 0.6;
        border-left: 4px solid #22c55e !important;
        background: #f0fdf4 !important;
      }
      .crv2-edit-dismissed {
        opacity: 0.4;
        border-left: 4px solid #94a3b8 !important;
        background: #f8fafc !important;
      }

      /* ─── In-Document Chat ─── */
      .crv2-chat-fab {
        position: fixed; bottom: 2rem; right: 2rem;
        width: 56px; height: 56px; border-radius: 50%;
        background: linear-gradient(135deg, #1e293b, #334155);
        color: white; display: flex; align-items: center; justify-content: center;
        box-shadow: 0 8px 32px rgba(15,23,42,0.3);
        cursor: pointer; transition: all 0.3s; z-index: 50;
        border: 2px solid rgba(255,255,255,0.1);
      }
      .crv2-chat-fab:hover { transform: scale(1.1); box-shadow: 0 12px 40px rgba(15,23,42,0.4); }
      .crv2-chat-panel {
        position: fixed; bottom: 6rem; right: 2rem;
        width: 420px; max-height: 600px;
        background: white; border-radius: 1.5rem;
        box-shadow: 0 20px 60px rgba(0,0,0,0.15);
        border: 1px solid #e2e8f0; z-index: 50;
        display: flex; flex-direction: column; overflow: hidden;
        animation: slideUp 0.3s ease;
      }
      @keyframes slideUp {
        from { opacity: 0; transform: translateY(20px); }
        to { opacity: 1; transform: translateY(0); }
      }

      /* ─── Citation Badges ─── */
      .crv2-citation {
        display: inline-flex; align-items: center; justify-content: center;
        background: #eff6ff; color: #1d4ed8; border: 1px solid #dbeafe;
        border-radius: 6px; padding: 0 6px; font-size: 10px; font-weight: 800;
        cursor: pointer; transition: all 0.2s; margin: 0 2px; vertical-align: middle;
        height: 18px; min-width: 18px; box-shadow: 0 1px 2px rgba(0,0,0,0.05);
      }
      .crv2-citation:hover {
        background: #1d4ed8; color: #ffffff; transform: scale(1.1);
        box-shadow: 0 4px 6px -1px rgba(29, 78, 216, 0.2);
      }
      @keyframes citationFlash {
        0%, 100% { background: transparent; }
        50% { background: rgba(250, 204, 21, 0.4); }
      }
      .citation-highlight {
        animation: citationFlash 1.5s ease 2;
        border-radius: 4px; padding: 2px 4px;
      }

      /* ─── Redline Styles ─── */
      .redline-del {
        color: #dc2626 !important;
        text-decoration: line-through !important;
        background-color: #fee2e2 !important;
        padding: 0 2px;
        border-radius: 2px;
      }
      .redline-ins {
        color: #16a34a !important;
        text-decoration: none !important;
        background-color: #dcfce7 !important;
        padding: 0 2px;
        border-radius: 2px;
        font-weight: 600;
      }
      .redline-ins.accepted {
        background-color: transparent !important;
        border-bottom: 2px solid #bbf7d0;
      }

      /* ─── Split Container (Step 4) ─── */
      .crv2-split-container {
        display: flex;
        gap: 2rem;
        height: 100%;
        margin-top: 0;
        padding-bottom: 0.5rem;
      }
      .crv2-left-panel {
        height: 100%;
        overflow-y: auto;
        padding-right: 1rem;
        scrollbar-width: thin;
        scroll-behavior: smooth;
      }
      .crv2-right-panel {
        height: 100%;
        background: #fff;
        border-radius: 1.5rem;
        border: 1px solid #e2e8f0;
        box-shadow: 0 10px 40px rgba(0,0,0,0.06);
        overflow: hidden;
        display: flex;
        flex-direction: column;
      }
      .crv2-right-panel .editor-wrapper {
        flex: 1;
        overflow: hidden;
      }

      /* ─── Resize Handle ─── */
      .crv2-resize-handle {
        width: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: col-resize;
        transition: all 0.2s;
        margin: 0 -4px;
        z-index: 20;
        position: relative;
      }
      .crv2-resize-handle:hover .crv2-handle-bar,
      .crv2-resize-handle[data-resize-handle-active] .crv2-handle-bar {
        background: #3b82f6;
        height: 60px;
        width: 4px;
      }
      .crv2-handle-bar {
        width: 2px;
        height: 32px;
        background: #cbd5e1;
        border-radius: 99px;
        transition: all 0.2s;
      }

      @media (max-width: 768px) {
        .crv2-root {
          height: auto !important;
          min-height: 100% !important;
          overflow-y: auto !important;
        }
        .crv2-header {
          padding: 0.75rem 1rem !important;
        }
        .crv2-stepper {
          gap: 0.5rem !important;
        }
        .crv2-step-label {
          display: none !important;
        }
        .crv2-step-connector {
          max-width: 14px !important;
        }
        .crv2-body {
          flex-direction: column !important;
          overflow: visible !important;
        }
        .crv2-form-panel {
          width: 100% !important;
          min-width: 0 !important;
          border-right: none !important;
          padding: 1rem !important;
          box-shadow: none !important;
        }
        .crv2-preview-panel {
          padding: 1rem !important;
          overflow: visible !important;
        }
        .crv2-preview-panel.step-4 {
          padding: 0.75rem 0.25rem !important;
        }
        .crv2-split-container {
          flex-direction: column !important;
          gap: 1rem !important;
          height: auto !important;
        }
        .crv2-left-panel {
          padding-right: 0 !important;
          height: auto !important;
          overflow: visible !important;
        }
        .crv2-right-panel {
          min-height: 520px !important;
        }
        .crv2-chat-fab {
          bottom: 5.5rem !important;
          right: 1rem !important;
        }
        .crv2-chat-panel {
          bottom: 9.5rem !important;
          right: 0.75rem !important;
          left: 0.75rem !important;
          width: auto !important;
          max-height: 65vh !important;
        }
      }
    `}</style>
    );
}

// ─── Step Definitions ────────────────────────────────────────────────────────

const STEPS = [
    { id: 1, label: "Cargar Contrato", icon: UploadCloud },
    { id: 2, label: "Playbook / Parámetros", icon: BookOpen },
    { id: 3, label: "Análisis Agente", icon: Shield },
    { id: 4, label: "Revisión", icon: CheckCircle2 },
];

// ─── Risk Heatmap Component ──────────────────────────────────────────────────

function RiskHeatmapChart({ data }: { data: Record<string, number> }) {
    const chartData = useMemo(() => {
        return Object.entries(data).map(([subject, score]) => ({
            subject,
            A: score,
            fullMark: 10,
        }));
    }, [data]);

    if (chartData.length === 0) return null;

    return (
        <div className="w-full h-[300px] mt-4 mb-8 bg-white/50 backdrop-blur-sm rounded-3xl border border-slate-200 p-4 shadow-sm overflow-hidden group">
            <div className="flex items-center gap-2 mb-2 px-2">
                <div className="p-1.5 bg-indigo-50 rounded-lg">
                    <Activity className="w-4 h-4 text-indigo-600" />
                </div>
                <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">Mapa de Calor de Riesgos por Categoría</h3>
            </div>
            <ResponsiveContainer width="100%" height="100%">
                <RadarChart cx="50%" cy="50%" outerRadius="80%" data={chartData}>
                    <PolarGrid stroke="#e2e8f0" />
                    <PolarAngleAxis
                        dataKey="subject"
                        tick={{ fill: '#64748b', fontSize: 10, fontWeight: 500 }}
                    />
                    <PolarRadiusAxis angle={30} domain={[0, 10]} tick={false} axisLine={false} />
                    <Radar
                        name="Riesgo"
                        dataKey="A"
                        stroke="#4f46e5"
                        strokeWidth={2}
                        fill="#4f46e5"
                        fillOpacity={0.15}
                    />
                    <RechartsTooltip
                        contentStyle={{
                            borderRadius: '16px',
                            border: 'none',
                            boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                            fontSize: '12px',
                            fontWeight: '600'
                        }}
                    />
                </RadarChart>
            </ResponsiveContainer>
        </div>
    );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function ContractReviewV2() {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const apiClient = useApiClient();
    const { startReviewRun, startDespachoReviewRun, getRun, cancelRun, resetRun } = useAgentExecution();
    const { openDocument } = useDocumentViewer();

    const isMobile = useIsMobile();
    const [mobileView, setMobileView] = useState<"form" | "results" | "editor">("form");
    const [step, setStep] = useState(1);
    const [isSidebarVisible, setIsSidebarVisible] = useState(true);
    const [activeTab, setActiveTab] = useState<"analysis" | "playbook" | "redline" | "editor" | "strategy" | "chat">("analysis");
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [contractType, setContractType] = useState<string>("contrato_prestacion_servicios");
    const [selectedSector, setSelectedSector] = useState<string>("general");
    const [options, setOptions] = useState<ReviewOptions>({
        riskFinancial: true,
        riskLegal: true,
        riskCompliance: true,
        clauseImportant: true,
        clauseObligations: true,
        clauseTermination: true,
    });
    const [contractContent, setContractContent] = useState<string>("");
    const [redlinedContent, setRedlinedContent] = useState<string>("");
    const [syncToken, setSyncToken] = useState<number>(0);
    const [isFetchingContent, setIsFetchingContent] = useState(false);
    const [currentDocId, setCurrentDocId] = useState<string | null>(null);
    const [fetchError, setFetchError] = useState<string | null>(null);

    // --- AUTO-EXECUTION FROM CHATBOT ---
    useEffect(() => {
        const prefill = (location.state as any)?.prefill;
        if (prefill && prefill.contract_text) {
            // 1. Update form
            setContractContent(prefill.contract_text);
            if (prefill.contract_type) setContractType(prefill.contract_type);
            // 2. Set UI to analysis mode
            setStep(3);
            // 3. Trigger analysis (short delay to ensure state is set)
            setTimeout(() => {
                handleAnalyze();
            }, 100);

            // Clean up state to prevent re-execution on refresh
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state]);


    // History state
    const [historyItems, setHistoryItems] = useState<HistoryItem[]>([]);
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [openedFromExpediente, setOpenedFromExpediente] = useState(false);

    // Despachos Integration State
    const [showProcesoDialog, setShowProcesoDialog] = useState(false);
    const [showFileDialog, setShowFileDialog] = useState(false);
    const [procesos, setProcesos] = useState<ScrapperProceso[]>([]);
    const [procesoFiles, setProcesoFiles] = useState<ProcesoArchivo[]>([]);
    const [selectedProceso, setSelectedProceso] = useState<ScrapperProceso | null>(null);
    const [selectedDespachoFile, setSelectedDespachoFile] = useState<ProcesoArchivo | null>(null);
    const [loadingProcesos, setLoadingProcesos] = useState(false);
    const [loadingFiles, setLoadingFiles] = useState(false);
    const [isDespachoAnalysis, setIsDespachoAnalysis] = useState(false);

    // New feature: Linking to internal Despachos Cases
    const { processes: internalProcesses } = useDespachos();
    const [selectedCaseId, setSelectedCaseId] = useState<string>("none");

    // ─── Legal Agent Features State ──────────────────────────────────────────
    // Playbook / Skill System
    const [playbook, setPlaybook] = useState<File | null>(null);
    const [playbookText, setPlaybookText] = useState<string>("");
    const [currentSkill, setCurrentSkill] = useState<PlaybookSkill | null>(null);
    const [savedSkills, setSavedSkills] = useState<PlaybookSkill[]>([]);
    const [isGeneratingSkill, setIsGeneratingSkill] = useState(false);
    const [isLoadingSkills, setIsLoadingSkills] = useState(false);
    const [showSkillSelector, setShowSkillSelector] = useState(false);
    const [playbookReviewResult, setPlaybookReviewResult] = useState<PlaybookReviewResult | null>(null);
    const [isReviewingWithPlaybook, setIsReviewingWithPlaybook] = useState(false);
    const [expandedTopicId, setExpandedTopicId] = useState<string | null>(null);

    // Accept/Dismiss Per-Edit
    const [editStates, setEditStates] = useState<Record<string, 'pending' | 'accepted' | 'accepted-comment' | 'dismissed'>>({});

    // In-Document Chat
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
    const [chatInput, setChatInput] = useState("");
    const [isChatLoading, setIsChatLoading] = useState(false);
    const chatEndRef = useRef<HTMLDivElement>(null);

    const reviewRunId = "contract-review-run";
    const currentRun = getRun(reviewRunId);

    // Synchronize extracted text to the editor during analysis for live visibility
    useEffect(() => {
        const extracted = currentRun?.metadata?.extracted_text;
        if (extracted && extracted !== contractContent && extracted.trim() !== "") {
            setContractContent(extracted);
        }
    }, [currentRun?.metadata?.extracted_text, contractContent]);


    const loading = currentRun?.status === "loading" || currentRun?.status === "streaming" || (isDespachoAnalysis && isUploading);
    const [localResult, setLocalResult] = useState<AnalysisResult | null>(null);
    const result = useMemo(() => {
        const rawMetadata = currentRun?.metadata;

        // --- PRIORITY FIX: If loading from history and not in an active run, prioritize localResult ---
        // We consider it "active" only if it's currently loading/streaming or has fresh analysis results
        const hasActiveAnalysis = rawMetadata && (rawMetadata.risk_analysis || rawMetadata.summary_bluf || rawMetadata.playbook_review);

        if (!loading && localResult && !hasActiveAnalysis) {
            return localResult;
        }

        if (!rawMetadata) return localResult;

        if (rawMetadata.risk_analysis || rawMetadata.summary_bluf || rawMetadata.playbook_review) {
            return rawMetadata as AnalysisResult;
        }

        if (rawMetadata.answer && typeof rawMetadata.answer === 'string') {
            let text = rawMetadata.answer;
            if (text.includes("```json")) {
                const match = text.match(/```json\s*([\s\S]*?)\s*```/);
                if (match) text = match[1];
            } else if (text.includes("```")) {
                const match = text.match(/```\s*([\s\S]*?)\s*```/);
                if (match) text = match[1];
            }

            if (text.includes("{")) {
                try {
                    const start = text.indexOf("{");
                    let jsonStr = text.slice(start);
                    const openBraces = (jsonStr.match(/{/g) || []).length;
                    const closeBraces = (jsonStr.match(/}/g) || []).length;
                    if (openBraces > closeBraces) {
                        jsonStr += "}".repeat(openBraces - closeBraces);
                    }
                    const parsed = JSON.parse(jsonStr);
                    if (parsed && (parsed.summary_bluf || parsed.risk_analysis || parsed.risk_heatmap)) {
                        return {
                            ...rawMetadata,
                            ...parsed,
                            answer: parsed.summary_bluf || parsed.summary || "Analizando..."
                        } as AnalysisResult;
                    }
                } catch (e) { }
            }
        }

        return (rawMetadata as AnalysisResult) || localResult;
    }, [currentRun?.metadata, localResult, loading]);

    // Auto-advance from step 3 (analysis) to step 4 (review) when analysis completes
    useEffect(() => {
        if (step === 3 && !loading && result && (result.risk_analysis || result.summary_bluf)) {
            setStep(4);
            if (isMobile) {
                setMobileView("results");
            }
        }
    }, [step, loading, result, isMobile]);

    const handleReset = useCallback(() => {
        setStep(1);
        if (isMobile) {
            setMobileView("form");
        }
        setActiveTab("analysis");
        setUploadedFile(null);
        setIsUploading(false);
        setOptions({
            riskFinancial: true,
            riskLegal: true,
            riskCompliance: true,
            clauseImportant: true,
            clauseObligations: true,
            clauseTermination: true,
        });
        setContractContent("");
        setRedlinedContent("");
        setIsFetchingContent(false);
        setCurrentDocId(null);
        setFetchError(null);
        setLocalResult(null);
        // Reset the execution run if possible - but startReviewRun with same ID will overwrite
        resetRun(reviewRunId);
        setIsDespachoAnalysis(false);
        setSelectedDespachoFile(null);
        setSelectedProceso(null);
        // Reset Legal Agent features
        setPlaybook(null);
        setPlaybookText("");
        setCurrentSkill(null);
        setPlaybookReviewResult(null);
        setEditStates({});
        setChatMessages([]);
        setExpandedTopicId(null);
        setSelectedSector("general");
    }, [resetRun]);

    // ─── Legal Agent: Playbook Handlers ──────────────────────────────────────

    const fetchSavedSkills = useCallback(async () => {
        setIsLoadingSkills(true);
        try {
            const res = await apiClient.get('/api/v1/contract-review/skills');
            if (res.ok) {
                setSavedSkills(res.data?.skills || []);
            }
        } catch (err) {
            console.error("Error fetching skills:", err);
        } finally {
            setIsLoadingSkills(false);
        }
    }, [apiClient]);

    // Fetch skills on mount for the tab visibility logic
    useEffect(() => {
        fetchSavedSkills();
    }, [fetchSavedSkills]);

    const handlePlaybookUpload = useCallback(async (file: File) => {
        setPlaybook(file);
        // Extract text from the playbook file
        const formData = new FormData();
        formData.append('file', file);
        try {
            // Use a simple reader for .docx or send to backend
            const text = await file.text();
            setPlaybookText(text);
        } catch (e) {
            // Fallback: read as text
            const reader = new FileReader();
            reader.onload = (e) => setPlaybookText(e.target?.result as string || "");
            reader.readAsText(file);
        }
    }, []);

    const handleGenerateSkill = useCallback(async () => {
        if (!playbookText) {
            toast.error("Por favor, suba un playbook primero.");
            return;
        }
        setIsGeneratingSkill(true);
        try {
            const res = await apiClient.post('/api/v1/contract-review/generate-skill', {
                playbook_text: playbookText,
                playbook_name: playbook?.name || "Playbook",
                source_filename: playbook?.name
            });
            if (res.ok) {
                const data = res.data;
                setCurrentSkill(data.skill);
                toast.success(data.message || "Skill generado exitosamente");
                fetchSavedSkills();
            } else {
                toast.error("Error al generar el skill");
            }
        } catch (err: any) {
            toast.error("Error de conexión: " + err.message);
        } finally {
            setIsGeneratingSkill(false);
        }
    }, [playbookText, playbook, fetchSavedSkills]);

    const handleLoadSkill = useCallback(async (skillId: string) => {
        try {
            const res = await apiClient.get(`/api/v1/contract-review/skills/${skillId}`);
            if (res.ok) {
                const data = res.data;
                setCurrentSkill(data.skill);
                setShowSkillSelector(false);
                toast.success(`Skill "${data.skill.name}" cargado`);
            }
        } catch (err) {
            toast.error("Error al cargar el skill");
        }
    }, []);

    const handleReviewWithPlaybook = useCallback(async () => {
        if (!currentSkill || !contractContent) {
            toast.error("Se necesita un skill y contenido del contrato.");
            return;
        }

        // Get the current record ID from the active result or metadata
        const reviewId = result?.db_id || result?.id;

        setIsReviewingWithPlaybook(true);
        try {
            const res = await apiClient.post('/api/v1/contract-review/review-with-skill', {
                contract_text: contractContent,
                skill_id: currentSkill.id,
                contract_type: contractType,
                review_id: reviewId
            });
            if (res.ok) {
                const data = res.data;
                setPlaybookReviewResult(data);
                setActiveTab("playbook");
                toast.success("Revisión contra playbook completada");
            } else {
                toast.error("Error en la revisión con playbook");
            }
        } catch (err: any) {
            toast.error("Error: " + err.message);
        } finally {
            setIsReviewingWithPlaybook(false);
        }
    }, [currentSkill, contractContent, contractType, result, apiClient]);

    // ─── Legal Agent: Accept/Dismiss Handlers ────────────────────────────────

    const handleAcceptEdit = useCallback((id: string | number, withComment: boolean = false) => {
        setEditStates(prev => ({
            ...prev,
            [String(id)]: withComment ? 'accepted-comment' : 'accepted'
        } as Record<string, "pending" | "accepted" | "accepted-comment" | "dismissed">));
        toast.success(withComment ? "Cambio aceptado con comentario" : "Cambio aceptado");
    }, []);

    const handleDismissEdit = useCallback((id: string | number) => {
        setEditStates(prev => ({
            ...prev,
            [String(id)]: 'dismissed'
        } as Record<string, "pending" | "accepted" | "accepted-comment" | "dismissed">));
        toast.info("Sugerencia descartada");
    }, []);

    const handleAcceptAllEdits = useCallback(() => {
        const newStates: Record<string, 'accepted'> = {};
        (result?.risk_analysis || []).forEach((_, i) => { newStates[`risk-${i}`] = 'accepted'; });
        (playbookReviewResult?.topics || []).forEach((topic, i) => {
            if (topic.suggested_edit) newStates[`pb-${i}`] = 'accepted';
        });
        setEditStates(prev => ({ ...prev, ...newStates }));
        toast.success("Todos los cambios aceptados");
    }, [result, playbookReviewResult]);

    // ─── Legal Agent: In-Document Chat ───────────────────────────────────────

    const handleSendChatMessage = useCallback(async () => {
        if (!chatInput.trim() || isChatLoading) return;

        const userMsg: ChatMessage = {
            id: Date.now().toString(),
            role: 'user',
            content: chatInput.trim()
        };
        setChatMessages(prev => [...prev, userMsg]);
        setChatInput("");
        setIsChatLoading(true);

        try {
            const res = await apiClient.post('/api/v1/contract-review/chat', {
                question: userMsg.content,
                contract_text: contractContent,
                analysis_result: result,
                history: chatMessages.map(m => ({ role: m.role, content: m.content }))
            });
            if (res.ok) {
                const data = res.data;
                const assistantMsg: ChatMessage = {
                    id: (Date.now() + 1).toString(),
                    role: 'assistant',
                    content: data.answer || data.response || "No se pudo generar una respuesta."
                };
                setChatMessages(prev => [...prev, assistantMsg]);
            }
        } catch (err) {
            setChatMessages(prev => [...prev, {
                id: (Date.now() + 1).toString(),
                role: 'assistant',
                content: "Error de conexión. Intenta de nuevo."
            }]);
        } finally {
            setIsChatLoading(false);
            setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
        }
    }, [chatInput, isChatLoading, contractContent]);

    // ─── Legal Agent: Citation Rendering ─────────────────────────────────────

    const renderWithCitations = useCallback((text: string) => {
        if (!text) return text;
        // Replace [N] with clickable citation elements
        return text.replace(/\[(\d+)\]/g, '<span class="crv2-citation" data-citation="$1" onclick="document.dispatchEvent(new CustomEvent(\'citation-click\', {detail: $1}))">$1</span>');
    }, []);

    // Handle Citation Click: Redirect to editor and highlight text
    useEffect(() => {
        const handler = (e: any) => {
            const index = e.detail - 1;

            // Check in Risk Analysis first
            const risks = result?.risk_analysis || [];
            let textToHighlight = "";

            if (risks[index]) {
                textToHighlight = risks[index].original_text;
            } else if (playbookReviewResult?.topics) {
                // If not in risks, check in playbook topics (citations from playbook report)
                // Finding by location index or similar
                const topicWithCitation = playbookReviewResult.topics.find(t =>
                    t.citations?.some((c: any) => c.location.includes(e.detail.toString()))
                );
                if (topicWithCitation) {
                    const citation = topicWithCitation.citations.find((c: any) => c.location.includes(e.detail.toString()));
                    if (citation) textToHighlight = citation.text;
                }
            }

            if (textToHighlight) {
                setActiveTab("editor");
                // Brief delay to let the tab render, then find and highlight via custom event
                setTimeout(() => {
                    window.dispatchEvent(new CustomEvent('editor-highlight', {
                        detail: { text: textToHighlight }
                    }));
                }, 400);
            }
        };
        document.addEventListener('citation-click', handler);
        return () => document.removeEventListener('citation-click', handler);
    }, [result, playbookReviewResult]);



    const handleOpenProcesoDialog = async () => {
        setShowProcesoDialog(true);
        setLoadingProcesos(true);
        try {
            // Query direct from procesos to show Active processes with Titles/Clients
            const { data, error } = await supabase
                .from('procesos')
                .select('*')
                .order('created_at', { ascending: false });

            if (error) throw error;

            // Map to the internal ScrapperProceso interface for compatibility
            const mappedProcesos = (data || []).map(p => ({
                numero_proceso: p.numero_proceso,
                radicado: p.numero_proceso,
                asunto: p.asunto || p.clase || 'Proceso sin título',
                metadata: { client_name: p.metadata?.client_name || '' }
            }));

            setProcesos(mappedProcesos);
        } catch (error) {
            toast.error("No se pudieron cargar los expedientes activos.");
        } finally {
            setLoadingProcesos(false);
        }
    };

    const handleSelectProceso = async (p: ScrapperProceso) => {
        setSelectedProceso(p);
        setShowProcesoDialog(false);
        setShowFileDialog(true);
        setLoadingFiles(true);
        try {
            const files = await getProcesoFiles(p.numero_proceso || p.radicado || "");
            setProcesoFiles(files);
        } catch (error) {
            toast.error("No se pudieron cargar los archivos del expediente.");
        } finally {
            setLoadingFiles(false);
        }
    };

    const handleSelectDespachoFile = (file: ProcesoArchivo) => {
        setSelectedDespachoFile(file);
        setShowFileDialog(false);
        setIsDespachoAnalysis(true);

        setCurrentDocId(file.file_name);
        setUploadedFile(new File([], file.file_name)); // Dummy for UI
        setContractContent(""); // Start empty, will be populated by stream

        setStep(2); // Go to options
    };

    const startDespachoAnalysis = async () => {
        if (!selectedProceso || !selectedDespachoFile) return;

        setIsDespachoAnalysis(true);
        setLocalResult(null);
        setStep(3);
        if (isMobile) setMobileView("results");

        try {
            const payload = {
                radicado: selectedProceso.numero_proceso || selectedProceso.radicado,
                file_name: selectedDespachoFile.file_name,
                contract_type: "contrato_prestacion_servicios",
                sector: selectedSector !== "general" ? selectedSector : undefined,
                playbook_review: playbookReviewResult,
                column_metadata: null
            };

            // Track usage for dashboard
            if (user) {
                UsageTracker.trackContractReview(user.id, "Análisis de despacho iniciado", "contrato_prestacion_servicios");
            }

            const agentData = getAgentBySlug("contract-review");
            await startDespachoReviewRun(
                reviewRunId,
                payload,
                apiClient,
                user?.id || null,
                agentData?.system_prompt
            );

        } catch (err: any) {
            toast.error(err.message);
        }
    };

    const fetchHistory = useCallback(async () => {
        setIsLoadingHistory(true);
        try {
            const session_resp = await supabase.auth.getSession();
            const token = session_resp.data.session?.access_token;
            if (!token) return;

            const res = await fetch(`${API_BASE_URL}/api/v1/contract-review/documents`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setHistoryItems(data.documents || []);
            } else {
                const errorData = await res.json().catch(() => ({}));
                toast.error(errorData.message || "Error al cargar el historial.");
            }
        } catch (err) {
            console.error("Error fetching history:", err);
            toast.error("Error de conexión al cargar el historial.");
        } finally {
            setIsLoadingHistory(false);
        }
    }, []);

    useEffect(() => {
        if (isHistoryOpen) {
            fetchHistory();
        }
    }, [isHistoryOpen, fetchHistory]);

    const handleLoadFromHistory = (item: HistoryItem) => {
        setLocalResult(item);
        // Clear previous content to trigger re-fetch of original text for redlines
        setContractContent("");
        setRedlinedContent("");
        setFetchError(null);

        // Restore playbook result if it exists
        let pbResult = item.playbook_review || (item as any).metadata?.playbook_review;

        // If it's a string (sometimes happens with JSONB in some clients), parse it
        if (typeof pbResult === 'string' && pbResult.trim().startsWith('{')) {
            try {
                pbResult = JSON.parse(pbResult);
            } catch (e) {
                console.error("Failed to parse playbook_review string from history:", e);
            }
        }

        if (pbResult && typeof pbResult === 'object') {
            setPlaybookReviewResult(pbResult as PlaybookReviewResult);
        } else {
            console.warn("No playbook review found in history item. Raw item:", item);
            // Check if it's nested even deeper or named differently
            const alternatePB = (item as any).playbook_review || (item as any).metadata?.playbook_review;
            if (alternatePB) {
                setPlaybookReviewResult(alternatePB as PlaybookReviewResult);
            } else {
                setPlaybookReviewResult(null);
            }
        }

        // Setting currentDocId will trigger useEffect to call fetchInitialContent
        // item.document_id now contains the real storage fileName from metadata (see backend change)
        const docId = item.document_id ||
            item.storage_file_name ||
            item.summary_pdf_url?.split('/').pop()?.split('?')[0] ||
            item.contract_url?.split('/').pop()?.split('?')[0];


        if (item.extracted_text) {
            setContractContent(item.extracted_text);
            if (docId) setCurrentDocId(docId);
        } else if (docId && docId !== item.db_id) {
            setCurrentDocId(docId);
        } else {
            // Fallback: if no valid storage docId found
            setContractContent(item.summary_bluf || item.summary || "");
        }

        setStep(3);
        setActiveTab("analysis");
        setIsHistoryOpen(false);
        toast.success("Análisis histórico cargado");
    };

    useEffect(() => {
        const historyItemId = (location.state as any)?.historyItemId;
        const linkedArtifact = (location.state as any)?.linkedArtifact;
        if (!historyItemId && !linkedArtifact) return;
        if ((location.state as any)?.fromExpediente) {
            setOpenedFromExpediente(true);
        }
        const openHistory = Boolean((location.state as any)?.openHistory);
        const loadLinkedHistory = async () => {
            setIsLoadingHistory(true);
            try {
                const session_resp = await supabase.auth.getSession();
                const token = session_resp.data.session?.access_token;
                if (!token) return;
                const res = await fetch(`${API_BASE_URL}/api/v1/contract-review/documents`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (!res.ok) throw new Error("No se pudo cargar historial");
                const data = await res.json();
                const docs = data.documents || [];
                setHistoryItems(docs);
                const match = docs.find((d: HistoryItem) => d.id === historyItemId);
                if (match) {
                    handleLoadFromHistory(match);
                    if (openHistory) setIsHistoryOpen(true);
                } else if (linkedArtifact) {
                    handleLoadFromHistory(linkedArtifact as HistoryItem);
                    if (openHistory) setIsHistoryOpen(true);
                    toast.success("Análisis vinculado cargado");
                } else {
                    toast.error("No se encontró el análisis en historial");
                }
            } catch (err) {
                console.error("Error loading linked contract history:", err);
                if (linkedArtifact) {
                    handleLoadFromHistory(linkedArtifact as HistoryItem);
                    if (openHistory) setIsHistoryOpen(true);
                    toast.success("Análisis vinculado cargado");
                } else {
                    toast.error("No fue posible abrir el análisis histórico");
                }
            } finally {
                setIsLoadingHistory(false);
                navigate(location.pathname, { replace: true, state: {} });
            }
        };
        loadLinkedHistory();
    }, [location.state, location.pathname, navigate]);

    const handleDeleteHistory = async (e: React.MouseEvent, id: string) => {
        e.stopPropagation(); // Don't load the item when clicking delete

        if (!confirm("¿Estás seguro de que deseas eliminar este análisis?")) return;

        try {
            const session_resp = await supabase.auth.getSession();
            const token = session_resp.data.session?.access_token;
            const res = await fetch(`${API_BASE_URL}/api/v1/contract-review/documents/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (res.ok) {
                toast.success("Análisis eliminado correctamente");
                setHistoryItems(prev => prev.filter(item => item.id !== id));
            } else {
                toast.error("No se pudo eliminar el análisis.");
            }
        } catch (err) {
            toast.error("Error de conexión al intentar eliminar.");
        }
    };

    // ─── Data Helpers ────────────────────────────────────────────────────────

    const riskCounts = useMemo(() => {
        const counts = { Alto: 0, Medio: 0, Bajo: 0 };
        (result?.risk_analysis || []).forEach(r => {
            if (counts[r.risk_level as keyof typeof counts] !== undefined) {
                counts[r.risk_level as keyof typeof counts]++;
            }
        });
        return counts;
    }, [result]);

    const fetchInitialContent = async (docId: string) => {
        setIsFetchingContent(true);
        setFetchError(null);
        try {
            const res = await apiClient.get(`/api/v1/documents/${encodeURIComponent(docId)}/initial-content?bucket=contract-analisis-raw-files`);
            if (res.ok) {
                const data = res.data;
                if (data.content) {
                    setContractContent(data.content);
                } else {
                    setFetchError("No se pudo extraer el texto del contrato.");
                }
            } else {
                setFetchError("Error al conectar con el servidor de contenido.");
            }
        } catch (err) {
            console.error("Error fetching initial content:", err);
            setFetchError("Error de red al recuperar el contenido.");
        } finally {
            setIsFetchingContent(false);
        }
    };

    const applyRedlinesToText = useCallback((baseText: string, risks: AnalysisResult['risk_analysis'], states: Record<string, string>, playbookResult: any) => {
        if (!baseText) return baseText;

        // Normalize base text to handle accents (NFC)
        let processed = baseText.normalize("NFC");

        // Combine risks from both sources
        const allEdits: Array<{ original: string, replacement: string, id: string, clauseName: string }> = [];

        // 1. Edits from Risk Analysis
        if (risks && risks.length > 0) {
            risks.forEach((risk, i) => {
                if (risk.original_text && risk.suggested_redline) {
                    allEdits.push({
                        original: risk.original_text,
                        replacement: risk.suggested_redline,
                        id: `risk-${i}`,
                        clauseName: risk.clause_name || `Riesgo ${i + 1}`
                    });
                }
            });
        }

        // 2. Edits from Playbook Review
        if (playbookResult?.topics) {
            playbookResult.topics.forEach((topic: any, i: number) => {
                if (topic.suggested_edit?.original && topic.suggested_edit?.replacement) {
                    allEdits.push({
                        original: topic.suggested_edit.original,
                        replacement: topic.suggested_edit.replacement,
                        id: `pb-${i}`,
                        clauseName: topic.topic_name || `Playbook ${i + 1}`
                    });
                }
            });
        }

        if (allEdits.length === 0) return processed;

        // Sort by length of original text descending to avoid nested replacement issues
        const sortedEdits = [...allEdits].sort((a, b) => (b.original?.length || 0) - (a.original?.length || 0));

        // Track which edits were successfully matched
        const matchedIds = new Set<string>();

        sortedEdits.forEach((edit) => {
            const state = states[edit.id] || 'pending';
            if (state === 'dismissed') {
                matchedIds.add(edit.id); // Count dismissed as "handled"
                return;
            }

            if (edit.original && edit.replacement) {
                const normalizedPattern = edit.original.normalize("NFC").trim();

                // Skip very short patterns or obvious "missing clause" descriptions
                const isMissing = normalizedPattern.length < 5;
                if (isMissing) return;

                const prevProcessed = processed;

                // Strategy 1: Exact match with flexible whitespace
                try {
                    const escaped = normalizedPattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                    const fuzzyRegexString = escaped.replace(/\s+/g, '[\\s\\n\\r]+');
                    const regex = new RegExp(fuzzyRegexString, 'gi');

                    processed = processed.replace(regex, (match) => {
                        matchedIds.add(edit.id);
                        if (state === 'accepted' || state === 'accepted-comment') {
                            return `<ins class="redline-ins accepted">${edit.replacement}</ins>`;
                        }
                        return `<del class="redline-del">${match}</del> <ins class="redline-ins">${edit.replacement}</ins>`;
                    });
                } catch (e) {
                    console.warn(`[Redline] Primary regex failed for edit ${edit.id}:`, e);
                }

                // Strategy 2: Fuzzy match using start+end anchors (wider range)
                if (processed === prevProcessed && normalizedPattern.length > 15) {
                    try {
                        const cleanSub = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '[\\s\\n\\r]+');
                        const anchorLen = Math.min(15, Math.floor(normalizedPattern.length / 3));
                        const start = cleanSub(normalizedPattern.substring(0, anchorLen));
                        const end = cleanSub(normalizedPattern.substring(normalizedPattern.length - anchorLen));
                        const fuzzyRegex = new RegExp(`${start}[\\s\\S]{1,2000}${end}`, 'gi');

                        processed = processed.replace(fuzzyRegex, (match) => {
                            matchedIds.add(edit.id);
                            if (state === 'accepted' || state === 'accepted-comment') {
                                return `<ins class="redline-ins accepted">${edit.replacement}</ins>`;
                            }
                            return `<del class="redline-del">${match}</del> <ins class="redline-ins">${edit.replacement}</ins>`;
                        });
                    } catch (e) {
                        console.warn(`[Redline] Fuzzy fallback failed for edit ${edit.id}:`, e);
                    }
                }

                // Strategy 3: Word-level subsequence match (most aggressive)
                if (processed === prevProcessed && normalizedPattern.length > 20) {
                    try {
                        // Extract key words (4+ chars) from the pattern
                        const words = normalizedPattern.match(/[a-záéíóúñü]{4,}/gi) || [];
                        if (words.length >= 3) {
                            // Use first 3 significant words as anchors
                            const keyWords = words.slice(0, 3).map(w =>
                                w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
                            );
                            const wordRegex = new RegExp(
                                keyWords.join('[\\s\\S]{0,200}'),
                                'gi'
                            );

                            // Find the match but be conservative - only use if it's reasonable length
                            const match = processed.match(wordRegex);
                            if (match && match[0] && match[0].length < normalizedPattern.length * 3) {
                                processed = processed.replace(wordRegex, (m) => {
                                    matchedIds.add(edit.id);
                                    if (state === 'accepted' || state === 'accepted-comment') {
                                        return `<ins class="redline-ins accepted">${edit.replacement}</ins>`;
                                    }
                                    return `<del class="redline-del">${m}</del> <ins class="redline-ins">${edit.replacement}</ins>`;
                                });
                            }
                        }
                    } catch (e) {
                        console.warn(`[Redline] Word-level fallback failed for edit ${edit.id}:`, e);
                    }
                }
            }
        });

        // --- Collect ALL unmatched edits (including "missing clause" descriptions) ---
        const unmatchedEdits = allEdits.filter(e => !matchedIds.has(e.id));

        if (unmatchedEdits.length > 0) {
            processed += `\n\n<div class="mt-8 p-6 bg-amber-50/50 border-2 border-dashed border-amber-200 rounded-2xl">`;
            processed += `<h3 class="text-sm font-bold text-amber-900 mb-2">📋 Sugerencias Adicionales (${unmatchedEdits.length})</h3>`;
            processed += `<p class="text-[10px] text-slate-500 mb-4 font-medium uppercase tracking-wider">Las siguientes sugerencias no pudieron ser ubicadas exactamente en el texto del contrato:</p>`;

            unmatchedEdits.forEach((edit) => {
                const state = states[edit.id] || 'pending';
                if (state === 'dismissed') return;

                processed += `<div class="mb-4 pt-4 border-t border-amber-100">`;
                processed += `<div class="text-[10px] font-bold text-amber-700 uppercase mb-2">${edit.clauseName}</div>`;
                if (edit.original && edit.original.length >= 5) {
                    processed += `<div class="p-2 bg-red-50 rounded mb-2"><del class="redline-del">${edit.original}</del></div>`;
                }
                processed += `<ins class="redline-ins ${state.startsWith('accepted') ? 'accepted' : ''}">${edit.replacement}</ins>`;
                processed += `</div>`;
            });
            processed += `</div>`;
        }

        // Convert newlines to <p> tags for Tiptap if it's just raw text
        if (!processed.includes('<p>')) {
            processed = processed.split('\n\n').map(p => `<p>${p.replace(/\n/g, '<br>')}</p>`).join('');
        }

        return processed;
    }, []);

    useEffect(() => {
        if (step === 3) {
            // Priority 1: Doc ID we tracked during upload
            // Priority 2: Extract from whatever metadata we have
            const docId = currentDocId || result?.summary_pdf_url?.match(/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i)?.[0];

            if (docId && !contractContent && !isFetchingContent && !fetchError) {
                fetchInitialContent(docId);
            }
        }
    }, [step, currentDocId, result, contractContent, isFetchingContent, fetchError]);

    useEffect(() => {
        if (contractContent) {
            setRedlinedContent(applyRedlinesToText(contractContent, result?.risk_analysis || [], editStates, playbookReviewResult));
        }
    }, [contractContent, result?.risk_analysis, editStates, playbookReviewResult, applyRedlinesToText]);

    useEffect(() => {
        if (result?.extracted_text && (!contractContent || contractContent.length < 100)) {
            setContractContent(result.extracted_text);
        }
    }, [result?.extracted_text, contractContent]);

    const cleanSummary = useMemo(() => {
        // Priority: Structured BLUF > Manual Answer > Fallback
        let text = result?.summary_bluf || result?.summary || currentRun?.metadata?.answer || "";

        if (typeof text !== 'string') return "";

        // Strip markdown code blocks if the AI wrapped the whole response
        if (text.includes("```json") || (text.includes("{") && text.includes("}"))) {
            try {
                const start = text.indexOf("{");
                const end = text.lastIndexOf("}") + 1;
                if (start !== -1 && end > start) {
                    const parsed = JSON.parse(text.slice(start, end));
                    return parsed.summary_bluf || parsed.summary || "Análisis técnico completado con éxito.";
                }
            } catch (e) {
                // If parsing fails, just strip the markers
                return text.replace(/```(?:json|markdown|txt|text)?\s*/gi, "").replace(/```/g, "").trim();
            }
        }

        // Final fallback: strip all markdown markers if they persist
        return text.replace(/```(?:json|markdown|txt|text)?\s*/gi, "").replace(/```/g, "").trim();
    }, [result, currentRun?.metadata?.answer]);

    // ─── Dropzone ───────────────────────────────────────────────────────────
    const onDrop = useCallback((acceptedFiles: File[]) => {
        if (acceptedFiles?.length > 0) {
            setUploadedFile(acceptedFiles[0]);
            setIsDespachoAnalysis(false);
            setStep(2);
            toast.success("Archivo cargado correctamente");
        }
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'application/pdf': ['.pdf'],
            'application/msword': ['.doc'],
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
        },
        maxFiles: 1
    });

    // ─── Submit ─────────────────────────────────────────────────────────────
    const handleAnalyze = async () => {
        if (!uploadedFile) {
            toast.error("Por favor, cargue un contrato primero.");
            return;
        }

        setIsUploading(true);
        setLocalResult(null);

        try {
            // 1. Upload file to Supabase Storage
            const fileExt = uploadedFile.name.split('.').pop();
            const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;
            const filePath = `contracts/${user?.id}/${fileName}`;
            setCurrentDocId(fileName); // Save for later content fetching
            setContractContent("");    // Reset
            setRedlinedContent("");
            setFetchError(null);

            const { data, error: uploadError } = await supabase.storage
                .from('contract-analisis-raw-files')
                .upload(filePath, uploadedFile);

            if (uploadError) throw uploadError;

            const { data: { publicUrl } } = supabase.storage
                .from('contract-analisis-raw-files')
                .getPublicUrl(filePath);

            // 2. Start review run in background
            const requestData = {
                fileUrl: publicUrl,
                options: options,
                case_id: selectedCaseId !== "none" ? selectedCaseId : undefined,
                file_name: uploadedFile.name,
                sector: selectedSector !== "general" ? selectedSector : undefined,
                playbook_review: playbookReviewResult, // Pass existing playbook review for persistence
                metadata: {
                    fileName: uploadedFile.name,
                    fileSize: uploadedFile.size,
                    fileType: uploadedFile.type
                }
            };

            // Transition to analysis view immediately
            setStep(3);
            if (isMobile) setMobileView("results");

            // Track usage for dashboard
            if (user) {
                UsageTracker.trackContractReview(user.id, "Análisis de archivo iniciado", contractType);
            }

            const agentData = getAgentBySlug("contract-review");
            await startReviewRun(reviewRunId, requestData, apiClient, user?.id || null, agentData?.system_prompt);

        } catch (error: any) {
            console.error("Upload/Review error:", error);
            toast.error("Error al procesar el contrato: " + error.message);
        } finally {
            setIsUploading(false);
        }
    };

    const handleDownload = async (format: 'pdf' | 'docx') => {
        if (!result) return;

        try {
            toast.loading(`Generando reporte ${format.toUpperCase()}...`);

            const response = await fetch(`${API_BASE_URL}/api/v1/contract-review/download-analysis`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
                },
                body: JSON.stringify({
                    analysis_data: result,
                    contract_type: uploadedFile?.name || "contrato",
                    format: format
                })
            });

            if (!response.ok) throw new Error("Error en la descarga");

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const date = new Date().toISOString().split('T')[0];
            const intent = (uploadedFile?.name || 'analisis').replace(/\.[^/.]+$/, "").replace(/\s+/g, '_');
            a.download = `Reporte_Legal_${intent}_${date}.${format}`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            toast.success("Reporte descargado correctamente");
        } catch (error: any) {
            toast.error("Error al descargar el reporte: " + error.message);
        } finally {
            toast.dismiss();
        }
    };

    // ─── Render ─────────────────────────────────────────────────────────────
    return (
        <div className="crv2-root">
            <PremiumStyles />

            {/* ── Header ── */}
            <div className="crv2-header">
                <div className="crv2-header-content">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-6">
                            <div className="flex items-center gap-4">
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-10 w-10 rounded-xl text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-all border border-transparent hover:border-blue-100 shadow-sm flex"
                                    onClick={() => navigate("/dashboard")}
                                    title="Regresar al Dashboard"
                                >
                                    <LayoutDashboard className="h-5 w-5" />
                                </Button>

                                <div className="w-px h-8 bg-slate-200 mx-1 block" />

                                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center shadow-lg shadow-blue-600/20 group transition-transform hover:scale-105">
                                    <Shield className="h-6 w-6 text-white group-hover:rotate-12 transition-transform" />
                                </div>
                                <div>
                                    <div className="flex items-center gap-3">
                                        <h1 className="text-xl font-extrabold text-slate-900 tracking-tight">
                                            Contract Review
                                        </h1>
                                        <span className="text-[10px] font-black bg-gradient-to-r from-amber-400 to-amber-600 text-white px-2.5 py-0.5 rounded-full shadow-sm border border-amber-500/20">
                                            PREMIUM
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2 mt-1">
                                        <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-lg bg-blue-50 border border-blue-100">
                                            <Sparkles className="h-3 w-3 text-blue-600" />
                                            <span className="text-[10px] font-bold text-blue-600 uppercase tracking-wider">
                                                {currentSkill?.name || "Analista Legal IA"}
                                            </span>
                                        </div>
                                        {uploadedFile && (
                                            <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-lg bg-green-50 border border-green-100 animate-in fade-in slide-in-from-left-2 duration-500">
                                                <FileText className="h-3 w-3 text-green-600" />
                                                <span className="text-[10px] font-bold text-green-700 truncate max-w-[200px]">
                                                    {uploadedFile.name}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            {step > 1 && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-10 rounded-2xl text-slate-500 hover:text-blue-600 hover:bg-blue-50 font-bold text-xs gap-2 px-4 transition-all"
                                    onClick={handleReset}
                                >
                                    <Plus className="h-4 w-4" /> Nueva Revisión
                                </Button>
                            )}

                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        variant="default"
                                        size="sm"
                                        className="bg-blue-600 text-white hover:bg-blue-700 gap-2 h-10 px-6 rounded-2xl shadow-md shadow-blue-600/20 font-bold text-xs transition-all hover:scale-105 active:scale-95 flex items-center"
                                    >
                                        <History className="h-4 w-4" /> Historial <ChevronDown className="h-3.5 w-3.5 opacity-80" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56 rounded-xl overflow-hidden shadow-xl border-slate-100 bg-white p-1">
                                    <DropdownMenuItem
                                        className="gap-2 px-3 py-2.5 focus:bg-blue-50 focus:text-blue-700 cursor-pointer text-xs font-semibold"
                                        onClick={() => setIsHistoryOpen(true)}
                                    >
                                        <History className="h-4 w-4 text-slate-500" /> Ver Historial
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator className="my-1 bg-slate-100" />
                                    <DropdownMenuItem
                                        disabled={!result}
                                        className="gap-2 px-3 py-2.5 focus:bg-blue-50 focus:text-blue-700 cursor-pointer text-xs font-semibold disabled:opacity-50 disabled:pointer-events-none"
                                        onClick={() => handleDownload('docx')}
                                    >
                                        <FileText className="h-4 w-4 text-slate-500" /> Memorando docx
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                        disabled={!result}
                                        className="gap-2 px-3 py-2.5 focus:bg-blue-50 focus:text-blue-700 cursor-pointer text-xs font-semibold disabled:opacity-50 disabled:pointer-events-none"
                                        onClick={() => handleDownload('pdf')}
                                    >
                                        <FileDown className="h-4 w-4 text-slate-500" /> Ficha tecnica
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>

                            <Sheet open={isHistoryOpen} onOpenChange={setIsHistoryOpen}>
                                <SheetContent side="right" className="w-[400px] sm:w-[500px] p-0 bg-slate-50 border-l border-slate-200 flex flex-col h-full">
                                    <SheetHeader className="p-6 bg-white border-b border-slate-100 flex-shrink-0">
                                        <SheetTitle className="flex items-center gap-2 text-xl font-bold text-slate-900">
                                            <History className="h-5 w-5 text-blue-600" />
                                            Historial de Análisis
                                        </SheetTitle>
                                        <SheetDescription className="text-slate-500">
                                            Consulta y recupera tus análisis de contratos anteriores.
                                        </SheetDescription>
                                        {openedFromExpediente && (
                                            <div className="mt-3 inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-2.5 py-1.5 text-[11px] font-semibold text-blue-700">
                                                <History className="h-3.5 w-3.5" />
                                                Registro abierto desde Actividad IA del expediente
                                            </div>
                                        )}
                                    </SheetHeader>

                                    <div className="flex-1 overflow-y-auto p-4 space-y-3">
                                        {isLoadingHistory ? (
                                            <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                                                <Loader2 className="h-8 w-8 animate-spin mb-4" />
                                                <p className="text-sm italic">Cargando historial...</p>
                                            </div>
                                        ) : historyItems.length === 0 ? (
                                            <div className="flex flex-col items-center justify-center py-20 text-center px-10">
                                                <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                                                    <Clock className="h-8 w-8 text-slate-300" />
                                                </div>
                                                <h3 className="text-slate-900 font-bold mb-1">Sin historial</h3>
                                                <p className="text-slate-500 text-xs">Aún no has realizado análisis de contratos.</p>
                                            </div>
                                        ) : (
                                            historyItems.map((item) => (
                                                <div
                                                    key={item.id}
                                                    onClick={() => handleLoadFromHistory(item)}
                                                    className="group bg-white border border-slate-200 hover:border-blue-500 hover:shadow-lg rounded-2xl p-5 transition-all cursor-pointer relative overflow-hidden border-l-4 border-l-blue-500 shadow-sm"
                                                >
                                                    <div className="flex justify-between items-start mb-3">
                                                        <div className="flex flex-col gap-1">
                                                            <h3 className="text-sm font-black text-slate-900 line-clamp-1 group-hover:text-blue-600 transition-colors">
                                                                {item.original_file_name || item.summary_bluf || "Análisis Técnico"}
                                                            </h3>
                                                            <div className="flex items-center gap-2">
                                                                <Badge variant="secondary" className="text-[9px] uppercase font-black bg-blue-50 text-blue-700 border-blue-100 px-1.5 py-0">
                                                                    {item.contract_type || "General"}
                                                                </Badge>
                                                                <span className="text-[9px] text-slate-400 font-bold flex items-center gap-1 uppercase tracking-tighter">
                                                                    <Clock className="h-2.5 w-2.5" />
                                                                    {new Date(item.created_at).toLocaleDateString()}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-8 w-8 text-slate-300 hover:text-red-500 hover:bg-red-50 transition-all rounded-lg"
                                                            onClick={(e) => handleDeleteHistory(e, item.id)}
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    </div>

                                                    <div className="flex items-center gap-4 mt-1">
                                                        <div className="flex items-center gap-1.5">
                                                            <div className={`w-2 h-2 rounded-full ${item.index_contractual_risk === 'Alto' ? 'bg-red-500' :
                                                                item.index_contractual_risk === 'Medio' ? 'bg-amber-500' : 'bg-green-500'
                                                                } shadow-sm shadow-slate-200`} />
                                                            <span className="text-[10px] font-black text-slate-600 uppercase tracking-tight">Riesgo {item.index_contractual_risk || 'Medio'}</span>
                                                        </div>
                                                        <div className="h-3 w-[1px] bg-slate-100" />
                                                        <div className="text-[10px] font-black text-slate-500 bg-slate-50 px-2 py-0.5 rounded-md border border-slate-100">
                                                            SCORE: {item.risk_score || 0}/100
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </SheetContent>
                            </Sheet>
                        </div>
                    </div>

                    {/* Stepper Progress Bar - Integrated at bottom of header */}
                    <div className="crv2-stepper-container">
                        <div className="crv2-stepper">
                            {[
                                { step: 1, label: "Cargar Documento", icon: <UploadCloud className="h-4 w-4" /> },
                                { step: 2, label: "Playbook / Parámetros", icon: <Settings2 className="h-4 w-4" /> },
                                { step: 3, label: "Análisis Agente", icon: <Sparkles className="h-4 w-4" /> },
                                { step: 4, label: "Revisión", icon: <CheckCircle2 className="h-4 w-4" /> },
                            ].map((s, i, arr) => (
                                <div key={s.step} style={{ display: 'contents' }}>
                                    <div
                                        className={`crv2-step ${step >= s.step ? 'active' : ''} ${step === s.step ? 'current' : ''}`}
                                    >
                                        <div className="crv2-step-icon">
                                            {step > s.step ? <Check className="h-4 w-4" /> : s.icon}
                                        </div>
                                        <span className="crv2-step-label">{s.label}</span>
                                    </div>
                                    {i < arr.length - 1 && (
                                        <div className={`crv2-step-connector ${step > s.step ? 'active' : ''}`} />
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Mobile View Switcher */}
            {isMobile && (
                <div className="bg-white/95 backdrop-blur-md border-b border-slate-200 px-3 py-2 flex items-center justify-between gap-1 shadow-sm sticky top-0 z-30">
                    <button
                        onClick={() => setMobileView("form")}
                        className={`flex-1 py-1.5 px-2 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                            mobileView === "form" ? "bg-slate-900 text-white shadow-sm" : "text-slate-600 hover:bg-slate-100"
                        }`}
                    >
                        <Settings2 className="h-3.5 w-3.5" /> Parámetros
                    </button>
                    <button
                        onClick={() => setMobileView("results")}
                        className={`flex-1 py-1.5 px-2 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                            mobileView === "results" ? "bg-blue-600 text-white shadow-sm" : "text-slate-600 hover:bg-slate-100"
                        }`}
                    >
                        <Shield className="h-3.5 w-3.5" /> Hallazgos
                    </button>
                    {result && (
                        <button
                            onClick={() => setMobileView("editor")}
                            className={`flex-1 py-1.5 px-2 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                                mobileView === "editor" ? "bg-indigo-600 text-white shadow-sm" : "text-slate-600 hover:bg-slate-100"
                            }`}
                        >
                            <FileText className="h-3.5 w-3.5" /> Editor
                        </button>
                    )}
                </div>
            )}

            {/* ── Body ── */}
            <div className="crv2-body relative">
                {!isSidebarVisible && !isMobile && (
                    <div className="absolute top-6 left-6 z-[30] animate-in fade-in zoom-in duration-300">
                        <Button
                            variant="secondary"
                            size="icon"
                            className="h-10 w-10 rounded-xl shadow-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-500 hover:text-blue-600 transition-all hover:scale-110 active:scale-95"
                            onClick={() => setIsSidebarVisible(true)}
                            title="Expandir panel lateral"
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /><line x1="9" y1="3" x2="9" y2="21" /></svg>
                        </Button>
                    </div>
                )}

                {/* ── Form Panel ── */}
                {(!isMobile || mobileView === "form") && (
                    <div className={`crv2-form-panel ${!isSidebarVisible && !isMobile ? 'collapsed' : ''}`}>
                    {isSidebarVisible && (
                        <div className="flex justify-between items-center mb-6 animate-in fade-in slide-in-from-top-4 duration-500">
                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Configuración del Análisis</span>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                                onClick={() => setIsSidebarVisible(false)}
                                title="Colapsar panel lateral"
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /><line x1="9" y1="3" x2="9" y2="21" /></svg>
                            </Button>
                        </div>
                    )}
                    {step === 1 && (
                        <div className="animate-in fade-in slide-in-from-left-4 duration-500">
                            <div className="crv2-field-group">
                                <div className="crv2-field-label"><Plus className="h-4 w-4" /> Nuevo Análisis</div>
                                <div {...getRootProps()} className={`crv2-dropzone ${isDragActive ? 'active' : ''}`}>
                                    <input {...getInputProps()} />
                                    <div className="flex flex-col items-center">
                                        <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
                                            <UploadCloud className="h-6 w-6 text-slate-400" />
                                        </div>
                                        <h3 className="text-base font-semibold text-slate-900 mb-1">Cargar Documento</h3>
                                        <p className="text-[12px] text-slate-500 max-w-xs mx-auto">
                                            Arrastra y suelta tu contrato o documento jurídico aquí o haz clic para buscarlo.
                                        </p>
                                        <div className="mt-3 flex gap-2">
                                            <span className="text-[9px] font-bold px-2 py-0.5 bg-slate-100 rounded text-slate-500 uppercase">PDF</span>
                                            <span className="text-[9px] font-bold px-2 py-0.5 bg-slate-100 rounded text-slate-500 uppercase">DOCX</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Despachos Integration Button */}
                                {/* <div className="mt-3 flex justify-center">
                                    <Button
                                        variant="outline"
                                        className="flex items-center gap-2 border-slate-200 hover:bg-slate-50 text-slate-700 font-medium py-4 px-6 rounded-xl shadow-sm transition-all h-auto"
                                        onClick={handleOpenProcesoDialog}
                                    >
                                        <Gavel className="h-4 w-4" />
                                        <span className="text-sm">Traer desde Despachos (Radicado)</span>
                                    </Button>
                                </div> */}

                                {/* ── Legal Agent: Playbook Upload ── */}
                                <div className="mt-4 border-t border-slate-100 pt-4">
                                    <div className="text-center mb-2">
                                        <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">— Revisión con Playbook —</span>
                                    </div>

                                    {!playbook && !currentSkill && (
                                        <div className="flex flex-col gap-2">
                                            <label className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl cursor-pointer hover:shadow-md transition-all group">
                                                <input
                                                    type="file"
                                                    className="hidden"
                                                    accept=".docx,.pdf,.txt"
                                                    onChange={(e) => {
                                                        const f = e.target.files?.[0];
                                                        if (f) handlePlaybookUpload(f);
                                                    }}
                                                />
                                                <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                                                    <BookOpen className="h-4 w-4 text-blue-600" />
                                                </div>
                                                <div className="flex-1">
                                                    <p className="text-sm font-bold text-slate-800">¿Tienes un Playbook?</p>
                                                    <p className="text-[10px] text-slate-500">Sube tu manual interno para revisión automatizada</p>
                                                </div>
                                                <ChevronRight className="h-4 w-4 text-slate-400" />
                                            </label>

                                            {savedSkills.length > 0 && (
                                                <Button
                                                    variant="outline"
                                                    className="w-full flex items-center gap-2 text-xs py-2 h-9"
                                                    onClick={() => setShowSkillSelector(true)}
                                                >
                                                    <FolderOpen className="h-3.5 w-3.5" />
                                                    Usar Skill Guardado ({savedSkills.length})
                                                </Button>
                                            )}

                                            <Button
                                                variant="ghost"
                                                className="text-[10px] text-slate-400 hover:text-blue-600 h-8"
                                                onClick={fetchSavedSkills}
                                            >
                                                <RotateCcw className="h-3 w-3 mr-1" /> Cargar skills guardados
                                            </Button>
                                        </div>
                                    )}

                                    {playbook && !currentSkill && (
                                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                                            <div className="flex items-center gap-2 mb-3">
                                                <BookOpen className="h-4 w-4 text-blue-600" />
                                                <span className="text-sm font-bold text-blue-800">{playbook.name}</span>
                                            </div>
                                            <Button
                                                onClick={handleGenerateSkill}
                                                disabled={isGeneratingSkill}
                                                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                                            >
                                                {isGeneratingSkill ? (
                                                    <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Generando Skill...</>
                                                ) : (
                                                    <><Sparkles className="h-4 w-4 mr-2" /> Generar Skill del Playbook</>
                                                )}
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="w-full mt-2 text-xs text-slate-500"
                                                onClick={() => { setPlaybook(null); setPlaybookText(""); }}
                                            >
                                                Cancelar
                                            </Button>
                                        </div>
                                    )}

                                    {currentSkill && (
                                        <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                                            <div className="flex items-center gap-2 mb-2">
                                                <CheckCircle2 className="h-4 w-4 text-green-600" />
                                                <span className="text-sm font-bold text-green-800">Skill Activo</span>
                                            </div>
                                            <p className="text-sm text-green-700 font-medium">{currentSkill.name}</p>
                                            <p className="text-xs text-green-600 mt-1">{currentSkill.topics?.length || 0} temas · {currentSkill.topics?.reduce((sum, t) => sum + (t.rules?.length || 0), 0)} reglas</p>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="mt-2 text-xs text-red-500 hover:text-red-700"
                                                onClick={() => setCurrentSkill(null)}
                                            >
                                                <X className="h-3 w-3 mr-1" /> Descargar Skill
                                            </Button>
                                        </div>
                                    )}
                                </div>

                                {/* Skill Selector Dialog */}
                                {showSkillSelector && (
                                    <Dialog open={showSkillSelector} onOpenChange={setShowSkillSelector}>
                                        <DialogContent className="sm:max-w-md">
                                            <DialogHeader>
                                                <DialogTitle className="flex items-center gap-2">
                                                    <BookOpen className="h-5 w-5 text-blue-600" />
                                                    Skills Guardados
                                                </DialogTitle>
                                                <DialogDescription>Selecciona un skill previamente generado</DialogDescription>
                                            </DialogHeader>
                                            <div className="space-y-2 max-h-[400px] overflow-y-auto">
                                                {savedSkills.map(s => (
                                                    <div
                                                        key={s.id}
                                                        onClick={() => handleLoadSkill(s.id)}
                                                        className="p-3 bg-slate-50 hover:bg-blue-50 border border-slate-200 hover:border-blue-300 rounded-lg cursor-pointer transition-all"
                                                    >
                                                        <p className="text-sm font-bold text-slate-800">{s.name}</p>
                                                        {s.description && <p className="text-xs text-slate-500 mt-0.5">{s.description}</p>}
                                                        {s.source_filename && <p className="text-[10px] text-slate-400 mt-1">📄 {s.source_filename}</p>}
                                                    </div>
                                                ))}
                                            </div>
                                        </DialogContent>
                                    </Dialog>
                                )}
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="animate-in fade-in slide-in-from-left-4 duration-500">
                            <div className="crv2-field-group">
                                <div className="crv2-field-label"><FileText className="h-4 w-4" /> Archivo Seleccionado</div>
                                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-dashed border-slate-200">
                                    <FileText className="h-8 w-8 text-slate-400" />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-slate-900 truncate">{uploadedFile?.name}</p>
                                        <p className="text-xs text-slate-500">{(uploadedFile?.size || 0) / 1024 > 1024 ? `${((uploadedFile?.size || 0) / 1024 / 1024).toFixed(2)} MB` : `${((uploadedFile?.size || 0) / 1024).toFixed(2)} KB`}</p>
                                    </div>
                                    <Button variant="ghost" size="icon" onClick={() => { setUploadedFile(null); setStep(1); }}>
                                        <Trash2 className="h-4 w-4 text-red-500" />
                                    </Button>
                                </div>
                            </div>

                            {/* <div className="crv2-field-group">
                                <div className="crv2-field-label"><FolderOpen className="h-4 w-4" /> Relacionar con Expediente (Opcional)</div>
                                <select
                                    className="w-full p-3 rounded-lg border border-slate-200 bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none"
                                    value={selectedCaseId}
                                    onChange={(e) => setSelectedCaseId(e.target.value)}
                                >
                                    <option value="none">-- No relacionar --</option>
                                    {internalProcesses.map(p => (
                                        <option key={p.id} value={p.id}>{p.case_number} - {p.title}</option>
                                    ))}
                                </select>
                            </div> */}

                            <div className="crv2-field-group">
                                <div className="crv2-field-label"><Globe className="h-4 w-4" /> Sector Económico (Opcional)</div>
                                <select
                                    className="w-full p-3 rounded-lg border border-slate-200 bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none mb-1"
                                    value={selectedSector}
                                    onChange={(e) => setSelectedSector(e.target.value)}
                                >
                                    <option value="general">Sin especialización (General)</option>
                                    <option value="servicios_publicos">Servicios Públicos (Energía, Agua, Gas, Telco)</option>
                                    <option value="salud_eps">Sector Salud (EPS, IPS, Invima)</option>
                                    <option value="manufactura">Manufactura y Suministros</option>
                                    <option value="agro">Agropecuario y Alimentos</option>
                                    <option value="construccion">Construcción e Inmobiliario</option>
                                    <option value="finanzas">Finanzas, Seguros y Banca</option>
                                    <option value="tecnologia">Tecnología, SaaS y Datos</option>
                                    <option value="educacion">Educación</option>
                                    <option value="retail">Comercio y Retail</option>
                                </select>
                                <p className="text-[10px] text-slate-400 mb-4">Selecciona el sector para un análisis con normativa especializada.</p>
                            </div>

                            <div className="crv2-field-group">
                                <div className="crv2-field-label"><Settings2 className="h-4 w-4" /> Opciones de Análisis</div>
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <div className="space-y-0.5">
                                            <Label className="text-sm font-semibold">Análisis de Riesgos</Label>
                                            <p className="text-xs text-slate-500">Identificar riesgos legales, financieros y de cumplimiento.</p>
                                        </div>
                                        <Checkbox checked={options.riskLegal} onCheckedChange={(v) => setOptions(o => ({ ...o, riskLegal: !!v, riskFinancial: !!v, riskCompliance: !!v }))} />
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <div className="space-y-0.5">
                                            <Label className="text-sm font-semibold">Extracción de Obligaciones</Label>
                                            <p className="text-xs text-slate-500">Listar compromisos y responsabilidades de las partes.</p>
                                        </div>
                                        <Checkbox checked={options.clauseObligations} onCheckedChange={(v) => setOptions(o => ({ ...o, clauseObligations: !!v }))} />
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <div className="space-y-0.5">
                                            <Label className="text-sm font-semibold">Cláusulas Críticas</Label>
                                            <p className="text-xs text-slate-500">Detectar terminación, confidencialidad y propiedad intelectual.</p>
                                        </div>
                                        <Checkbox checked={options.clauseImportant} onCheckedChange={(v) => setOptions(o => ({ ...o, clauseImportant: !!v, clauseTermination: !!v }))} />
                                    </div>
                                </div>
                            </div>

                            {/* ── Phase 1: Skill Editor Panel (when playbook/skill loaded) ── */}
                            {currentSkill && (
                                <div className="crv2-field-group border-indigo-200 bg-indigo-50/30">
                                    <div className="crv2-field-label text-indigo-700">
                                        <BookOpen className="h-4 w-4" /> Skill Editor — {currentSkill.name}
                                    </div>
                                    <p className="text-[10px] text-slate-500 mb-3">
                                        {currentSkill.topics?.length || 0} temas · {currentSkill.topics?.reduce((sum, t) => sum + (t.rules?.length || 0), 0)} reglas totales
                                    </p>

                                    <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                                        {currentSkill.topics?.map((topic, ti) => (
                                            <div key={topic.id} className="bg-white rounded-lg border border-indigo-100 p-3">
                                                <div
                                                    className="flex items-center justify-between cursor-pointer"
                                                    onClick={() => setExpandedTopicId(expandedTopicId === `skill-${ti}` ? null : `skill-${ti}`)}
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-2 h-2 rounded-full bg-indigo-400" />
                                                        <span className="text-xs font-bold text-slate-800">{topic.name}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-[9px] text-slate-400 font-medium">{topic.rules?.length || 0} reglas</span>
                                                        {expandedTopicId === `skill-${ti}` ? <ChevronUp className="h-3 w-3 text-slate-400" /> : <ChevronDown className="h-3 w-3 text-slate-400" />}
                                                    </div>
                                                </div>

                                                {expandedTopicId === `skill-${ti}` && (
                                                    <div className="mt-3 pt-3 border-t border-indigo-50 space-y-2 animate-in fade-in duration-200">
                                                        {topic.description && (
                                                            <p className="text-[10px] text-slate-500 italic">{topic.description}</p>
                                                        )}
                                                        {topic.rules?.map((rule, ri) => (
                                                            <div key={ri} className="flex items-start gap-2 text-[11px] text-slate-700">
                                                                <CheckCircle2 className="h-3 w-3 text-indigo-400 mt-0.5 flex-shrink-0" />
                                                                <span>{rule.text || (rule as any)}</span>
                                                            </div>
                                                        ))}
                                                        {topic.example_clauses?.length > 0 && (
                                                            <div className="mt-2 pt-2 border-t border-indigo-50">
                                                                <span className="text-[9px] font-bold text-indigo-500 uppercase">Ejemplos:</span>
                                                                {topic.example_clauses.map((ex, ei) => (
                                                                    <p key={ei} className="text-[10px] text-slate-500 italic mt-1 pl-2 border-l-2 border-indigo-200">
                                                                        {ex.text || (ex as any)}
                                                                    </p>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {topic.legal_framework && (
                                                            <p className="text-[9px] text-indigo-600 font-medium mt-1">📚 {topic.legal_framework}</p>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>

                                    <div className="flex gap-2 mt-3">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="flex-1 text-xs text-red-500 hover:text-red-700 hover:bg-red-50"
                                            onClick={() => setCurrentSkill(null)}
                                        >
                                            <X className="h-3 w-3 mr-1" /> Remover Skill
                                        </Button>
                                    </div>
                                </div>
                            )}

                            <Button
                                className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-bold gap-2 shadow-lg shadow-slate-200"
                                onClick={isDespachoAnalysis ? startDespachoAnalysis : handleAnalyze}
                                disabled={isUploading || loading}
                            >
                                {isUploading || loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5" />}
                                {isDespachoAnalysis ? "Analizar Documento de Despacho" : "Iniciar Análisis Inteligente"}
                            </Button>
                        </div>
                    )}

                    {(step === 3 || step === 4) && (
                        <div className="animate-in fade-in slide-in-from-left-4 duration-500">
                            <div className="crv2-field-group">
                                <div className="crv2-field-label"><Shield className="h-4 w-4" /> Estado del Análisis</div>
                                <div className="space-y-6 py-4">
                                    <div className="flex flex-col items-center text-center">
                                        {loading ? (
                                            <>
                                                <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4 relative">
                                                    <Loader2 className="h-8 w-8 text-slate-400 animate-spin" />
                                                </div>
                                                <h3 className="font-bold text-slate-900">{isDespachoAnalysis ? "Procesando Expediente" : "Análisis en Curso"}</h3>
                                                <p className="text-xs text-slate-500 mt-1">Nuestra IA está extrayendo y validando cláusulas...</p>
                                            </>
                                        ) : result ? (
                                            <>
                                                <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mb-4">
                                                    <CheckCircle2 className="h-8 w-8 text-green-500" />
                                                </div>
                                                <h3 className="font-bold text-slate-900">Análisis Completado</h3>
                                                <p className="text-xs text-slate-500 mt-1">Se han identificado {(result.risk_analysis?.length || result.risks?.length || 0)} riesgos potenciales.</p>
                                            </>
                                        ) : (
                                            <>
                                                <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
                                                    <AlertCircle className="h-8 w-8 text-red-500" />
                                                </div>
                                                <h3 className="font-bold text-slate-900">Hubo un Problema</h3>
                                                <p className="text-xs text-slate-500 mt-1">No se pudo completar el análisis.</p>
                                            </>
                                        )}
                                    </div>

                                    {currentRun?.traceSteps && currentRun.traceSteps.length > 0 && (
                                        <div className="space-y-3 mt-4">
                                            {currentRun.traceSteps.map((s, i) => (
                                                <div key={i} className="flex items-center gap-3">
                                                    <span className="text-lg">{s.emoji}</span>
                                                    <span className={`text-xs font-semibold ${s.status === 'running' ? 'text-slate-900' : 'text-slate-500'}`}>{s.message}</span>
                                                    {s.status === 'running' && <Loader2 className="h-3 w-3 animate-spin ml-auto" />}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {!loading && result && (
                                <Button
                                    variant="outline"
                                    className="w-full h-11 border-slate-200 rounded-xl gap-2 text-slate-700"
                                    onClick={handleReset}
                                >
                                    <RotateCcw className="h-4 w-4" /> Nuevo Análisis
                                </Button>
                            )}
                        </div>
                    )}
                </div>
                )}

                {/* ── Preview Panel ── */}
                {(!isMobile || mobileView !== "form") && (
                    <div className={`crv2-preview-panel ${step === 4 ? 'step-4' : ''}`}>
                    {!result && !loading ? (
                        <div className="crv2-welcome">
                            <div className="crv2-welcome-icon">
                                <Shield className="h-10 w-10 text-white" />
                            </div>
                            <h2 className="text-2xl font-extrabold text-slate-900">Bienvenido al Reviewer V2</h2>
                            <p className="text-slate-500 mt-2 max-w-sm">
                                Cargue un contrato para obtener un desglose detallado de riesgos, obligaciones y cumplimiento legal.
                            </p>
                        </div>
                    ) : loading ? (
                        <div className="crv2-welcome max-w-2xl mx-auto">
                            <div className="w-24 h-24 rounded-[2rem] bg-white shadow-2xl flex items-center justify-center relative overflow-hidden mb-8 border border-slate-100">
                                <div className="absolute inset-0 bg-gradient-to-tr from-blue-50 to-transparent animate-pulse" />
                                <Loader2 className="h-10 w-10 text-blue-600 animate-spin relative z-10" />
                            </div>
                            <h2 className="text-2xl font-black text-slate-900 tracking-tight">Procesando Inteligencia Legal...</h2>
                            <p className="text-slate-500 text-sm mt-2 font-medium">Ejecutando orquestación multiactores y verificación normativa</p>

                            <div className="w-full max-w-md mt-10 space-y-6">
                                <div className="space-y-2">
                                    <div className="flex justify-between text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                        <span>Progreso del Motor</span>
                                        <span>{Math.min(95, (currentRun?.traceSteps?.length || 0) * 12)}%</span>
                                    </div>
                                    <div className="h-2 w-full bg-white border border-slate-100 rounded-full overflow-hidden shadow-inner p-0.5">
                                        <div
                                            className="h-full bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full transition-all duration-700 ease-out"
                                            style={{ width: `${Math.min(95, (currentRun?.traceSteps?.length || 0) * 12)}%` }}
                                        />
                                    </div>
                                </div>

                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="mx-auto border-red-100 text-red-500 hover:bg-red-50 hover:text-red-600 font-black text-[10px] tracking-widest rounded-xl h-10 px-6 mt-2"
                                    onClick={() => cancelRun(reviewRunId)}
                                >
                                    <X className="h-3 w-3 mr-2" /> CANCELAR ANÁLISIS
                                </Button>

                                {currentRun?.metadata?.answer && (
                                    <div className="p-6 bg-white/70 backdrop-blur-xl border border-white rounded-3xl shadow-xl shadow-blue-50/50 animate-in fade-in slide-in-from-bottom-4 duration-1000">
                                        <div className="flex items-center gap-2 mb-3 border-b border-slate-50 pb-3">
                                            <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Generación en Tiempo Real</span>
                                        </div>
                                        <p className="text-xs text-slate-600 leading-relaxed font-medium line-clamp-4 italic">
                                            "{cleanSummary}"
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : result.status === 'rejected' ? (
                        <div className="max-w-2xl mx-auto py-12 animate-in fade-in slide-in-from-bottom-4 duration-700">
                            <div className="crv2-result-card border-t-4 border-t-red-500 shadow-2xl shadow-red-100/50">
                                <div className="flex items-start gap-5 mb-8">
                                    <div className="w-14 h-14 rounded-2xl bg-red-100 flex items-center justify-center text-red-600 flex-shrink-0 shadow-inner">
                                        <ShieldAlert className="h-8 w-8" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-black text-slate-900 mb-1">Análisis No Autorizado</h3>
                                        <p className="text-sm text-slate-500 leading-relaxed">
                                            Nuestros sistemas de seguridad han determinado que este documento no cumple con los requisitos técnicos para un análisis contractual clínico.
                                        </p>
                                    </div>
                                </div>

                                <div className="space-y-4 p-5 bg-slate-50 rounded-2xl border border-slate-100 mb-8">
                                    <div className="flex justify-between items-center pb-3 border-b border-slate-200">
                                        <div className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Criterio de Validación</div>
                                        <div className="px-2 py-0.5 bg-red-100 text-red-700 text-[10px] font-black rounded-full uppercase">Rechazado</div>
                                    </div>

                                    <div className="flex justify-between items-center text-sm">
                                        <span className="text-slate-600 font-bold">Confianza de Detección</span>
                                        <span className="text-slate-900 font-black">{Math.round((result.guard_rail_details?.confidence || 0) * 100)}%</span>
                                    </div>

                                    <div className="pt-2">
                                        <div className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2">Motivo Técnico</div>
                                        <p className="text-xs text-slate-700 italic font-medium leading-relaxed bg-white p-3 rounded-lg border border-slate-200">
                                            "{result.guard_rail_details?.reason || result.message}"
                                        </p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <Button
                                        variant="outline"
                                        className="h-12 rounded-xl border-slate-200 text-slate-600 font-bold hover:bg-slate-50 transition-all"
                                        onClick={handleReset}
                                    >
                                        <RotateCcw className="h-4 w-4 mr-2" /> REINTENTAR
                                    </Button>
                                    <Button
                                        className="h-12 rounded-xl bg-slate-900 text-white font-bold hover:bg-slate-800 shadow-lg shadow-slate-200 transition-all"
                                        onClick={() => navigate('/dashboard')}
                                    >
                                        VOLVER AL PANEL
                                    </Button>
                                </div>
                            </div>

                            <div className="mt-8 p-6 rounded-3xl bg-blue-50 border border-blue-100 flex gap-4 items-center">
                                <Info className="h-5 w-5 text-blue-500 flex-shrink-0" />
                                <p className="text-[11px] text-blue-700 font-medium leading-relaxed">
                                    <b>Nota de Optimización:</b> Esta validación automática previene el consumo innecesario de créditos de inteligencia artificial en documentos que no son contratos (ej: fotos, facturas, cartas generales o documentos corruptos).
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="w-full h-full animate-in fade-in zoom-in-95 duration-500">
                            <div className="crv2-split-container">
                                <PanelGroup direction={isMobile ? "vertical" : "horizontal"}>
                                    {/* Panel Izquierdo: Análisis y Resultados */}
                                    {(!isMobile || mobileView === "results") && (
                                        <Panel defaultSize={isMobile ? 100 : 40} minSize={isMobile ? 100 : 25}>
                                            <div className="crv2-left-panel">
                                            {/* Advanced Stats Grid */}
                                            <div className={`grid ${step === 4 ? 'grid-cols-2' : 'grid-cols-4'} gap-3 mb-6`}>
                                                <div className="crv2-result-card text-center">
                                                    <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Impacto General</div>
                                                    <div className="flex justify-center">
                                                        <span className={`crv2-risk-badge crv2-risk-${(result.index_contractual_risk || result.risk_level || 'medio').toLowerCase()}`}>
                                                            {result.index_contractual_risk || result.risk_level || 'Medio'}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="crv2-result-card text-center">
                                                    <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Puntaje Riesgo</div>
                                                    <div className="text-2xl font-black text-slate-900">{result.risk_score || 0}%</div>
                                                </div>
                                                <div className="crv2-result-card text-center">
                                                    <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Confianza Jurídica</div>
                                                    <div className="text-2xl font-black text-blue-600">{result.confidence_indicator?.score || 85}%</div>
                                                </div>
                                                <div className="crv2-result-card text-center">
                                                    <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Conformidad</div>
                                                    <div className="text-2xl font-black text-green-600">{result.compliance_check?.overall_status === 'Cumple' ? 'ALTA' : 'MEDIA'}</div>
                                                </div>
                                            </div>

                                            <div className="flex items-center justify-between mb-6">
                                                <div className="flex border-b border-slate-200 gap-8 w-full overflow-x-auto pb-1">
                                                    {[
                                                        { id: 'analysis', label: 'Análisis Clínico', icon: '⚖️' },
                                                        ...(playbookReviewResult || result?.playbook_review || currentSkill || playbook || isGeneratingSkill || savedSkills.length > 0 ? [{ id: 'playbook', label: 'Cumplimiento Playbook', icon: '📋' }] : []),
                                                        { id: 'redline', label: 'Propuesta Redacción', icon: '✏️' },
                                                        // { id: 'editor', label: 'Editor de redlines', icon: '📝' },
                                                        { id: 'strategy', label: 'Estrategia', icon: '🛡️' },
                                                        // { id: 'chat', label: 'Chat Legal', icon: '💬' }
                                                    ].map(tab => {
                                                        // Debug tab visibility in console
                                                        if (tab.id === 'playbook') {
                                                        }
                                                        return (
                                                            <button
                                                                key={tab.id}
                                                                onClick={() => setActiveTab(tab.id as any)}
                                                                className={`pb-3 text-sm font-bold flex items-center gap-2 transition-all relative whitespace-nowrap ${activeTab === tab.id ? 'text-slate-900 border-b-2 border-slate-900' : 'text-slate-400 hover:text-slate-600'
                                                                    }`}
                                                            >
                                                                <span className="text-lg">{tab.icon}</span>
                                                                <span className="hidden lg:inline">{tab.label}</span>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>

                                            {activeTab === "analysis" && (
                                                <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-500">
                                                    {/* Summary BLUF with Citations */}
                                                    <div className="crv2-result-card border-l-4 border-l-slate-900">
                                                        <div className="flex items-center gap-2 mb-3">
                                                            <ScrollText className="h-5 w-5 text-slate-700" />
                                                            <h3 className="font-bold text-slate-900 uppercase text-xs tracking-widest">Resumen Ejecutivo (BLUF)</h3>
                                                        </div>
                                                        <div
                                                            className="text-sm text-slate-700 leading-relaxed font-medium prose prose-slate prose-sm max-w-none"
                                                            dangerouslySetInnerHTML={{
                                                                __html: cleanSummary
                                                                    ? renderWithCitations(marked.parse(cleanSummary, { async: false }) as string)
                                                                    : "Generando análisis clínico..."
                                                            }}
                                                        />
                                                    </div>

                                                    {/* Visual Risk Heatmap (New Enhancement) */}
                                                    {result.risk_heatmap && (
                                                        <div className="animate-in fade-in duration-1000 delay-300">
                                                            <RiskHeatmapChart data={result.risk_heatmap} />
                                                        </div>
                                                    )}

                                                    {/* Risk Heatmap Simulation */}
                                                    <div className="crv2-result-card">
                                                        <div className="flex justify-between items-center mb-4">
                                                            <div className="crv2-field-label">Mapa de Calor: Distribución de Riesgo</div>
                                                            <div className="px-2 py-0.5 bg-slate-100 rounded text-[10px] font-bold text-slate-600">
                                                                TOTAL: {(result.risk_analysis || []).length} HALLAZGOS
                                                            </div>
                                                        </div>
                                                        <div className="flex gap-1.5 h-10 w-full rounded-xl overflow-hidden border border-slate-100 mt-2 shadow-inner bg-slate-50">
                                                            {riskCounts.Alto > 0 ? (
                                                                <div
                                                                    className="h-full bg-red-500 hover:brightness-110 transition-all cursor-help flex items-center justify-center text-white text-[10px] font-bold"
                                                                    style={{ width: `${Math.max(15, (riskCounts.Alto / (result.risk_analysis?.length || 1)) * 100)}%` }}
                                                                    title={`Riesgo Alto: ${riskCounts.Alto}`}
                                                                >
                                                                    {riskCounts.Alto}
                                                                </div>
                                                            ) : null}
                                                            {riskCounts.Medio > 0 ? (
                                                                <div
                                                                    className="h-full bg-amber-500 hover:brightness-110 transition-all cursor-help flex items-center justify-center text-white text-[10px] font-bold"
                                                                    style={{ width: `${Math.max(15, (riskCounts.Medio / (result.risk_analysis?.length || 1)) * 100)}%` }}
                                                                    title={`Riesgo Medio: ${riskCounts.Medio}`}
                                                                >
                                                                    {riskCounts.Medio}
                                                                </div>
                                                            ) : null}
                                                            {riskCounts.Bajo > 0 ? (
                                                                <div
                                                                    className="h-full bg-green-500 hover:brightness-110 transition-all cursor-help flex items-center justify-center text-white text-[10px] font-bold"
                                                                    style={{ width: `${Math.max(15, (riskCounts.Bajo / (result.risk_analysis?.length || 1)) * 100)}%` }}
                                                                    title={`Riesgo Bajo: ${riskCounts.Bajo}`}
                                                                >
                                                                    {riskCounts.Bajo}
                                                                </div>
                                                            ) : null}
                                                            {!result.risk_analysis?.length && <div className="w-full h-full flex items-center justify-center text-slate-300 text-[10px] font-medium italic">Sin riesgos detectados</div>}
                                                        </div>
                                                        <div className="flex justify-between mt-3 text-[10px] font-bold text-slate-400 px-1">
                                                            <span className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-red-500 shadow-sm shadow-red-200" /> IMPACTO ALTO ({riskCounts.Alto})</span>
                                                            <span className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-amber-500 shadow-sm shadow-amber-200" /> IMPACTO MEDIO ({riskCounts.Medio})</span>
                                                            <span className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-green-500 shadow-sm shadow-green-200" /> IMPACTO BAJO ({riskCounts.Bajo})</span>
                                                        </div>
                                                    </div>

                                                    {/* Detailed Risk Analysis */}
                                                    <div className="space-y-5">
                                                        {(result.risk_analysis || []).map((risk: any, index: number) => {
                                                            const severityColor = risk.severity === 'Alto' ? 'red' : risk.severity === 'Medio' ? 'amber' : 'green';
                                                            return (
                                                                <div key={index} className="crv2-result-card group/risk hover:shadow-lg transition-all duration-300 cursor-pointer"
                                                                    onClick={() => {
                                                                        setTimeout(() => {
                                                                            window.dispatchEvent(new CustomEvent('editor-highlight', {
                                                                                detail: { text: risk.original_text }
                                                                            }));
                                                                        }, 300);
                                                                    }}
                                                                >
                                                                    <div className="flex items-start justify-between mb-4">
                                                                        <div className="flex items-center gap-3">
                                                                            <div className={`w-3 h-3 rounded-full bg-${severityColor}-500 shadow-sm shadow-${severityColor}-200 ring-4 ring-${severityColor}-50`} />
                                                                            <div>
                                                                                <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Cláusula</div>
                                                                                <div className="text-sm font-bold text-slate-800">{risk.clause || `Hallazgo ${index + 1}`}</div>
                                                                            </div>
                                                                        </div>
                                                                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-${severityColor}-100 text-${severityColor}-700 border border-${severityColor}-200`}>
                                                                            {risk.severity || 'Medio'}
                                                                        </span>
                                                                    </div>

                                                                    <div className="space-y-4">
                                                                        <div className="p-4 bg-slate-50/50 rounded-2xl border border-slate-100">
                                                                            <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Problema Detectado</div>
                                                                            <p className="text-sm text-slate-700 leading-relaxed font-medium"
                                                                                dangerouslySetInnerHTML={{ __html: renderWithCitations(risk.problem_detected) }}
                                                                            />
                                                                        </div>

                                                                        <div className="p-4 bg-indigo-50/30 rounded-2xl border border-indigo-100/50">
                                                                            <div className="text-[10px] font-black text-indigo-600 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                                                                                <Scale className="h-3.5 w-3.5" /> Fundamento Jurídico
                                                                            </div>
                                                                            <p className="text-[11px] font-serif italic text-slate-600 leading-relaxed font-medium">
                                                                                "{risk.legal_basis}"
                                                                            </p>
                                                                        </div>

                                                                        {(risk.benchmark || risk.playbook_link) && (
                                                                            <div className="grid grid-cols-2 gap-3 mt-2">
                                                                                {risk.benchmark && (
                                                                                    <div className="p-3 bg-amber-50/50 border border-amber-100 rounded-xl cursor-help group/bench"
                                                                                        onClick={(e) => { e.stopPropagation(); }}
                                                                                    >
                                                                                        <div className="text-[9px] font-black text-amber-600 uppercase tracking-widest mb-1 flex items-center gap-1">
                                                                                            <Activity className="h-2.5 w-2.5" /> Benchmark de Mercado
                                                                                        </div>
                                                                                        <p className="text-[10px] text-amber-800 font-medium leading-tight">
                                                                                            {risk.benchmark}
                                                                                        </p>
                                                                                    </div>
                                                                                )}
                                                                                {risk.playbook_link && (
                                                                                    <div className="p-3 bg-blue-50/50 border border-blue-100 rounded-xl group/link cursor-pointer hover:bg-blue-100/50 transition-colors"
                                                                                        onClick={(e) => { e.stopPropagation(); }}
                                                                                    >
                                                                                        <div className="text-[9px] font-black text-blue-600 uppercase tracking-widest mb-1 flex items-center gap-1">
                                                                                            <BookOpen className="h-2.5 w-2.5" /> Playbook Interno
                                                                                        </div>
                                                                                        <p className="text-[10px] text-blue-800 font-bold flex items-center gap-1">
                                                                                            {risk.playbook_link} <ExternalLink className="h-2 w-2 opacity-0 group-hover/link:opacity-100 transition-opacity" />
                                                                                        </p>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}

                                            {activeTab === "redline" && (
                                                <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-500">
                                                    <div className="crv2-field-label">✏️ Propuestas de Redacción Alternativa</div>

                                                    {/* Combined Proposals: Risks + Playbook Suggestions */}
                                                    {(() => {
                                                        const proposals: Array<{
                                                            id: string,
                                                            title: string,
                                                            originalText: string,
                                                            suggestedText: string,
                                                            source: 'risk' | 'playbook',
                                                            sourceLabel: string,
                                                            sourceColor: string
                                                        }> = [];

                                                        // 1. Add Risk Analysis Proposals
                                                        (result.risk_analysis || []).forEach((risk, i) => {
                                                            if (risk.original_text || risk.suggested_redline) {
                                                                proposals.push({
                                                                    id: `risk-${i}`,
                                                                    title: risk.clause_name || `Riesgo ${i + 1}`,
                                                                    originalText: risk.original_text,
                                                                    suggestedText: risk.suggested_redline,
                                                                    source: 'risk',
                                                                    sourceLabel: 'Análisis Clínico',
                                                                    sourceColor: 'text-slate-500 bg-slate-100'
                                                                });
                                                            }
                                                        });

                                                        // 2. Add Playbook Suggestions
                                                        const pbRes = playbookReviewResult || result?.playbook_review;
                                                        (pbRes?.topics || []).forEach((topic, i) => {
                                                            if (topic.suggested_edit?.original || topic.suggested_edit?.replacement) {
                                                                proposals.push({
                                                                    id: `pb-${i}`,
                                                                    title: topic.topic_name || `Playbook ${i + 1}`,
                                                                    originalText: topic.suggested_edit.original,
                                                                    suggestedText: topic.suggested_edit.replacement,
                                                                    source: 'playbook',
                                                                    sourceLabel: 'Playbook',
                                                                    sourceColor: 'text-indigo-600 bg-indigo-50'
                                                                });
                                                            }
                                                        });

                                                        if (proposals.length === 0) {
                                                            return (
                                                                <div className="crv2-result-card text-center py-12">
                                                                    <div className="text-slate-400 italic text-sm">No se identificaron propuestas de redacción en este análisis.</div>
                                                                </div>
                                                            );
                                                        }

                                                        return proposals.map((prop) => (
                                                            <div
                                                                key={prop.id}
                                                                className="crv2-result-card cursor-pointer hover:border-blue-300 transition-all group"
                                                                onClick={(e) => {
                                                                    if ((e.target as HTMLElement).closest('button')) return;
                                                                    setTimeout(() => {
                                                                        window.dispatchEvent(new CustomEvent('editor-highlight', {
                                                                            detail: { text: prop.originalText }
                                                                        }));
                                                                    }, 300);
                                                                }}
                                                            >
                                                                <div className="flex items-center justify-between mb-4">
                                                                    <h4 className="font-bold text-slate-900 text-sm">{prop.title}</h4>
                                                                    <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded tracking-widest ${prop.sourceColor}`}>
                                                                        {prop.sourceLabel}
                                                                    </span>
                                                                </div>

                                                                <div className="space-y-4">
                                                                    {prop.originalText && (
                                                                        <div className="p-4 bg-red-50/30 rounded-lg border border-red-100/50">
                                                                            <div className="text-[10px] font-bold text-red-500 uppercase mb-2">Cláusula Original</div>
                                                                            <p className="text-xs text-slate-600 italic">"{prop.originalText}"</p>
                                                                        </div>
                                                                    )}

                                                                    <div className="p-4 bg-green-50/30 rounded-lg border border-green-100/50 relative group">
                                                                        <div className="flex items-center justify-between mb-2">
                                                                            <div className="text-[10px] font-bold text-green-600 uppercase">Redacción Sugerida (Redline)</div>
                                                                            <div className="flex gap-2">
                                                                                {editStates[prop.id] === 'pending' || !editStates[prop.id] ? (
                                                                                    <>
                                                                                        <Button
                                                                                            size="sm"
                                                                                            className="h-7 px-3 text-[10px] font-bold bg-green-600 hover:bg-green-700 text-white rounded-lg gap-1.5"
                                                                                            onClick={() => handleAcceptEdit(prop.id)}
                                                                                        >
                                                                                            <Check className="h-3 w-3" /> ACEPTAR
                                                                                        </Button>
                                                                                        <Button
                                                                                            variant="ghost"
                                                                                            size="sm"
                                                                                            className="h-7 px-3 text-[10px] font-bold text-slate-400 hover:text-red-500 rounded-lg gap-1.5"
                                                                                            onClick={() => handleDismissEdit(prop.id)}
                                                                                        >
                                                                                            <X className="h-3 w-3" /> DESCARTAR
                                                                                        </Button>
                                                                                    </>
                                                                                ) : (
                                                                                    <div className="flex items-center gap-2">
                                                                                        <span className={`text-[10px] font-bold uppercase px-3 py-1 rounded-lg ${editStates[prop.id]?.startsWith('accepted') ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'
                                                                                            }`}>
                                                                                            {editStates[prop.id]?.startsWith('accepted') ? '✓ Aceptado' : '✗ Descartado'}
                                                                                        </span>
                                                                                        <Button
                                                                                            variant="ghost"
                                                                                            size="sm"
                                                                                            className="h-7 w-7 p-0 text-slate-400 hover:text-slate-600"
                                                                                            onClick={() => setEditStates(prev => ({ ...prev, [prop.id]: 'pending' }))}
                                                                                        >
                                                                                            <RotateCcw className="h-3.5 w-3.5" />
                                                                                        </Button>
                                                                                    </div>
                                                                                )}
                                                                                <Button
                                                                                    variant="ghost"
                                                                                    size="sm"
                                                                                    className="h-7 w-7 p-0 text-slate-400 hover:text-blue-500"
                                                                                    onClick={() => {
                                                                                        setTimeout(() => {
                                                                                            window.dispatchEvent(new CustomEvent('editor-highlight', {
                                                                                                detail: { text: prop.originalText }
                                                                                            }));
                                                                                        }, 300);
                                                                                    }}
                                                                                    title="Ver en Editor"
                                                                                >
                                                                                    <ExternalLink className="h-3.5 w-3.5" />
                                                                                </Button>
                                                                                <Button
                                                                                    variant="ghost"
                                                                                    size="sm"
                                                                                    className="h-7 w-7 p-0 text-slate-400 hover:text-blue-500"
                                                                                    onClick={() => {
                                                                                        navigator.clipboard.writeText(prop.suggestedText);
                                                                                        toast.success("Copiado al portapapeles");
                                                                                    }}
                                                                                >
                                                                                    <Copy className="h-3.5 w-3.5" />
                                                                                </Button>
                                                                            </div>
                                                                        </div>
                                                                        <p className="text-xs text-slate-800 font-medium leading-relaxed">{prop.suggestedText}</p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ));
                                                    })()}
                                                </div>
                                            )}

                                            {activeTab === "strategy" && (
                                                <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-500">
                                                    <div className="grid grid-cols-2 gap-6">
                                                        <div className="crv2-result-card">
                                                            <div className="crv2-field-label">Estrategia de Negociación</div>
                                                            <div className="mt-4 space-y-4">
                                                                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                                                                    <span className="text-xs font-bold text-slate-500">Posición de Contraparte:</span>
                                                                    <span className="text-xs font-black text-slate-900 uppercase">
                                                                        {result.negotiation_strategy?.counterparty_position || 'MODERADA'}
                                                                    </span>
                                                                </div>

                                                                <div className="space-y-2">
                                                                    <div className="text-[10px] font-bold text-slate-400 uppercase">Cláusulas No Negociables</div>
                                                                    <div className="flex flex-wrap gap-2">
                                                                        {(result.negotiation_strategy?.critical_clauses || ['Terminación', 'Garantías']).map((c, i) => (
                                                                            <span key={i} className="px-2 py-1 bg-slate-900 text-white text-[10px] font-bold rounded">
                                                                                {c}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div className="crv2-result-card">
                                                            <div className="crv2-field-label">Validación Normativa</div>
                                                            <div className="mt-4 space-y-3">
                                                                {(result.compliance_check?.regulations || [
                                                                    { norma: 'Ley 820 de 2003', status: '✔ Cumple' },
                                                                    { norma: 'Código Civil', status: '⚠ Ajustes' },
                                                                    { norma: 'Ley 1581 (Datos)', status: '✔ Cumple' }
                                                                ]).map((reg, i) => (
                                                                    <div key={i} className="flex items-center justify-between p-2 border-b border-slate-50 last:border-0">
                                                                        <span className="text-xs font-medium text-slate-700">{reg.norma}</span>
                                                                        <span className={`text-[10px] font-bold ${reg.status.includes('✔') ? 'text-green-600' : 'text-amber-600'}`}>
                                                                            {reg.status}
                                                                        </span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="crv2-result-card">
                                                        <div className="crv2-field-label">Recomendaciones Estratégicas</div>
                                                        {!(result.negotiation_strategy?.recommendations?.length || result.recommendations?.length) && (
                                                            <div className="mt-4 text-xs text-slate-400 italic font-medium">No se generaron recomendaciones tácticas específicas para este contrato.</div>
                                                        )}
                                                        <ul className="mt-4 space-y-3">
                                                            {(result.negotiation_strategy?.recommendations || result.recommendations || [
                                                                "Priorizar la aclaración de las cláusulas de responsabilidad.",
                                                                "Negociar términos de terminación más flexibles.",
                                                                "Validar el cumplimiento de la Ley 1581 en el tratamiento de datos."
                                                            ]).map((rec, i) => (
                                                                <li key={i} className="flex gap-3 text-xs text-slate-700 leading-relaxed">
                                                                    <div className="w-5 h-5 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0 text-[10px] font-bold text-blue-600 border border-blue-100 shadow-sm">{i + 1}</div>
                                                                    {rec}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>

                                                    <div className="p-6 bg-slate-900 rounded-3xl text-white shadow-xl shadow-slate-200 border border-slate-800">
                                                        <div className="flex items-center gap-2 mb-4">
                                                            <Info className="h-4 w-4 text-blue-400" />
                                                            <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Explicabilidad del Análisis</h3>
                                                        </div>
                                                        <div className="grid grid-cols-4 gap-4">
                                                            {(result.explainability?.methodology || [
                                                                "Extracción de Cláusulas",
                                                                "Comparación Normativa",
                                                                "Análisis de Riesgos",
                                                                "Generación de Estrategia"
                                                            ]).map((step, i) => (
                                                                <div key={i} className="text-center group">
                                                                    <div className="text-[10px] font-black text-slate-600 mb-1">STEP 0{i + 1}</div>
                                                                    <div className="text-[10px] font-bold text-white group-hover:text-blue-400 transition-colors leading-tight">{step}</div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* ── Phase 1: Cumplimiento Playbook Tab ── */}
                                            {activeTab === "playbook" && (
                                                <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-500">
                                                    {/* Playbook Review Header */}
                                                    <div className="crv2-result-card border-l-4 border-l-indigo-500">
                                                        <div className="flex items-center justify-between mb-4">
                                                            <div className="flex items-center gap-3">
                                                                <BookOpen className="h-5 w-5 text-indigo-600" />
                                                                <div>
                                                                    <h3 className="font-bold text-slate-900 text-sm">
                                                                        {playbookReviewResult?.skill_name || result?.playbook_review?.skill_name || currentSkill?.name || "Cumplimiento de Playbook"}
                                                                    </h3>
                                                                    <p className="text-xs text-slate-500 mt-0.5">
                                                                        {playbookReviewResult?.summary || result?.playbook_review?.summary || "Ejecuta la revisión contra el playbook cargado para ver resultados."}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            {!playbookReviewResult && currentSkill && (
                                                                <Button
                                                                    onClick={handleReviewWithPlaybook}
                                                                    disabled={isReviewingWithPlaybook || !contractContent}
                                                                    className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold gap-2 h-10 px-6"
                                                                >
                                                                    {isReviewingWithPlaybook ? (
                                                                        <><Loader2 className="h-4 w-4 animate-spin" /> Revisando...</>
                                                                    ) : (
                                                                        <><Shield className="h-4 w-4" /> Revisar contra Playbook</>
                                                                    )}
                                                                </Button>
                                                            )}
                                                            {!playbookReviewResult && !currentSkill && (
                                                                <div className="flex gap-2">
                                                                    {playbook ? (
                                                                        <Button
                                                                            onClick={handleGenerateSkill}
                                                                            disabled={isGeneratingSkill}
                                                                            className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold gap-2 h-10 px-6"
                                                                        >
                                                                            {isGeneratingSkill ? (
                                                                                <><Loader2 className="h-4 w-4 animate-spin" /> Generando...</>
                                                                            ) : (
                                                                                <><Sparkles className="h-4 w-4" /> Generar Skill</>
                                                                            )}
                                                                        </Button>
                                                                    ) : (
                                                                        <Button
                                                                            onClick={() => setShowSkillSelector(true)}
                                                                            className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold gap-2 h-10 px-6"
                                                                        >
                                                                            <FolderOpen className="h-4 w-4" /> Seleccionar Skill
                                                                        </Button>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>

                                                        {(playbookReviewResult || result?.playbook_review) && (
                                                            <div className="flex gap-3 mt-2">
                                                                {['green', 'light-red', 'dark-red', 'gray'].map(status => {
                                                                    const res = playbookReviewResult || result?.playbook_review;
                                                                    const count = res?.topics?.filter(t => t.status === status).length || 0;
                                                                    if (count === 0) return null;
                                                                    const labels: Record<string, string> = { 'green': 'Cumple', 'light-red': 'Ajustes', 'dark-red': 'Crítico', 'gray': 'No Aplica' };
                                                                    const colors: Record<string, string> = { 'green': 'bg-green-100 text-green-700 border-green-200', 'light-red': 'bg-orange-100 text-orange-700 border-orange-200', 'dark-red': 'bg-red-100 text-red-700 border-red-200', 'gray': 'bg-slate-100 text-slate-500 border-slate-200' };
                                                                    return (
                                                                        <div key={status} className={`px-3 py-1.5 rounded-lg border text-[10px] font-black uppercase ${colors[status]}`}>
                                                                            {labels[status]}: {count}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Topic Cards */}
                                                    {(playbookReviewResult || result?.playbook_review) ? (
                                                        <div className="space-y-3">
                                                            {(playbookReviewResult?.topics || result?.playbook_review?.topics || []).map((topic, i) => (
                                                                <div
                                                                    key={topic.topic_id}
                                                                    className={`crv2-topic-card crv2-topic-${topic.status}`}
                                                                    onClick={(e) => {
                                                                        // Prevent expansion if clicking a button
                                                                        if ((e.target as HTMLElement).closest('button')) return;
                                                                        setExpandedTopicId(expandedTopicId === topic.topic_id ? null : topic.topic_id);
                                                                    }}
                                                                >
                                                                    <div className="flex items-center gap-3">
                                                                        <div className={`crv2-topic-dot ${topic.status}`} />
                                                                        <div className="flex-1">
                                                                            <h4 className="font-bold text-sm">{topic.topic_name}</h4>
                                                                            <p className="text-xs opacity-80 mt-0.5 line-clamp-1">{topic.analysis}</p>
                                                                        </div>
                                                                        {expandedTopicId === topic.topic_id ? <ChevronUp className="h-4 w-4 opacity-50" /> : <ChevronDown className="h-4 w-4 opacity-50" />}
                                                                    </div>

                                                                    {expandedTopicId === topic.topic_id && (
                                                                        <div className="mt-4 pt-4 border-t border-current/10 space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
                                                                            {/* Full Analysis */}
                                                                            <div>
                                                                                <div className="text-[10px] font-black uppercase tracking-widest mb-2 opacity-60">Análisis Completo</div>
                                                                                <p className="text-xs leading-relaxed">{topic.analysis}</p>
                                                                            </div>

                                                                            {/* Rules Evaluated */}
                                                                            {topic.rules_evaluated?.length > 0 && (
                                                                                <div>
                                                                                    <div className="text-[10px] font-black uppercase tracking-widest mb-2 opacity-60">Reglas Evaluadas</div>
                                                                                    <div className="space-y-1.5">
                                                                                        {topic.rules_evaluated.map((r, ri) => (
                                                                                            <div key={ri} className="flex items-start gap-2 text-xs">
                                                                                                <span className={`mt-0.5 ${r.compliant === 'yes' ? 'text-green-600' : r.compliant === 'partial' ? 'text-orange-600' : 'text-red-600'}`}>
                                                                                                    {r.compliant === 'yes' ? '✓' : r.compliant === 'partial' ? '⚠' : '✗'}
                                                                                                </span>
                                                                                                <div>
                                                                                                    <span className="font-semibold">{r.rule}</span>
                                                                                                    {r.detail && <span className="text-xs opacity-70 ml-1">— {r.detail}</span>}
                                                                                                </div>
                                                                                            </div>
                                                                                        ))}
                                                                                    </div>
                                                                                </div>
                                                                            )}

                                                                            {/* Suggested Edit */}
                                                                            {topic.suggested_edit && (
                                                                                <div
                                                                                    className="p-3 bg-white/50 rounded-xl border border-current/10 cursor-pointer hover:bg-white/80 transition-all"
                                                                                    onClick={(e) => {
                                                                                        if ((e.target as HTMLElement).closest('button')) return;
                                                                                        setActiveTab("editor");
                                                                                        setTimeout(() => {
                                                                                            window.dispatchEvent(new CustomEvent('editor-highlight', {
                                                                                                detail: { text: topic.suggested_edit.original }
                                                                                            }));
                                                                                        }, 300);
                                                                                    }}
                                                                                >
                                                                                    <div className="flex items-center justify-between mb-2">
                                                                                        <div className="text-[10px] font-black uppercase tracking-widest opacity-60">Edición Sugerida</div>

                                                                                        <div className="flex gap-2">
                                                                                            {editStates[`pb-${i}`] === 'pending' || !editStates[`pb-${i}`] ? (
                                                                                                <>
                                                                                                    <Button
                                                                                                        size="sm"
                                                                                                        className="h-6 px-2 text-[9px] font-bold bg-green-600 hover:bg-green-700 text-white rounded-md gap-1"
                                                                                                        onClick={(e) => { e.stopPropagation(); handleAcceptEdit(`pb-${i}`); }}
                                                                                                    >
                                                                                                        <Check className="h-2.5 w-2.5" /> ACEPTAR
                                                                                                    </Button>
                                                                                                    <Button
                                                                                                        variant="ghost"
                                                                                                        size="sm"
                                                                                                        className="h-6 px-2 text-[9px] font-bold text-slate-400 hover:text-red-500 rounded-md gap-1"
                                                                                                        onClick={(e) => { e.stopPropagation(); handleDismissEdit(`pb-${i}`); }}
                                                                                                    >
                                                                                                        <X className="h-2.5 w-2.5" /> DESCARTAR
                                                                                                    </Button>
                                                                                                </>
                                                                                            ) : (
                                                                                                <div className="flex items-center gap-2">
                                                                                                    <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded ${editStates[`pb-${i}`]?.startsWith('accepted') ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'
                                                                                                        }`}>
                                                                                                        {editStates[`pb-${i}`]?.startsWith('accepted') ? '✓ Aceptado' : '✗ Descartado'}
                                                                                                    </span>
                                                                                                    <Button
                                                                                                        variant="ghost"
                                                                                                        size="sm"
                                                                                                        className="h-6 w-6 p-0 text-slate-400 hover:text-slate-600"
                                                                                                        onClick={(e) => { e.stopPropagation(); setEditStates(prev => ({ ...prev, [`pb-${i}`]: 'pending' })); }}
                                                                                                    >
                                                                                                        <RotateCcw className="h-3 w-3" />
                                                                                                    </Button>
                                                                                                </div>
                                                                                            )}
                                                                                        </div>
                                                                                    </div>

                                                                                    <div className="space-y-2">
                                                                                        <div className="p-2 rounded-lg bg-red-50/50">
                                                                                            <div className="text-[9px] font-bold text-red-500 uppercase mb-1">Original</div>
                                                                                            <p className="text-xs text-red-800 italic">"{topic.suggested_edit.original}"</p>
                                                                                        </div>
                                                                                        <div className="p-2 rounded-lg bg-green-50/50">
                                                                                            <div className="text-[9px] font-bold text-green-600 uppercase mb-1">Reemplazo</div>
                                                                                            <p className="text-xs text-green-800 font-medium">{topic.suggested_edit.replacement}</p>
                                                                                        </div>
                                                                                        {topic.suggested_edit.comment && (
                                                                                            <p className="text-[10px] text-slate-500 italic mt-1">💡 {topic.suggested_edit.comment}</p>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            )}

                                                                            {/* Citations */}
                                                                            {topic.citations?.length > 0 && (
                                                                                <div className="flex flex-wrap gap-1.5">
                                                                                    {topic.citations.map((c, ci) => (
                                                                                        <span
                                                                                            key={ci}
                                                                                            className="text-[9px] px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-bold cursor-pointer hover:bg-blue-200 transition-colors flex items-center gap-1"
                                                                                            title={c.text}
                                                                                            onClick={() => {
                                                                                                setActiveTab("editor");
                                                                                                setTimeout(() => {
                                                                                                    window.dispatchEvent(new CustomEvent('editor-highlight', {
                                                                                                        detail: { text: c.text }
                                                                                                    }));
                                                                                                }, 300);
                                                                                            }}
                                                                                        >
                                                                                            <ExternalLink className="h-2.5 w-2.5" /> {c.location}
                                                                                        </span>
                                                                                    ))}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : !currentSkill ? (
                                                        <div className="crv2-result-card text-center py-12">
                                                            <BookOpen className="h-12 w-12 text-slate-200 mx-auto mb-4" />
                                                            <h3 className="text-lg font-bold text-slate-800 mb-2">Sin Playbook Cargado</h3>
                                                            <p className="text-sm text-slate-500 max-w-sm mx-auto mb-6">
                                                                Para ver el informe de cumplimiento, primero sube un playbook en el paso "Cargar Contrato" y genera un Skill.
                                                            </p>
                                                            <Button variant="outline" onClick={() => setStep(1)} className="gap-2">
                                                                <BookOpen className="h-4 w-4" /> Ir a Cargar Playbook
                                                            </Button>
                                                        </div>
                                                    ) : (
                                                        <div className="crv2-result-card text-center py-12">
                                                            <Shield className="h-12 w-12 text-indigo-200 mx-auto mb-4" />
                                                            <h3 className="text-lg font-bold text-slate-800 mb-2">Skill Listo para Revisión</h3>
                                                            <p className="text-sm text-slate-500 max-w-sm mx-auto mb-6">
                                                                Tienes el skill "{currentSkill.name}" cargado con {currentSkill.topics?.length || 0} temas. Haz clic para revisar el contrato contra el playbook.
                                                            </p>
                                                            <Button
                                                                onClick={handleReviewWithPlaybook}
                                                                disabled={isReviewingWithPlaybook || !contractContent}
                                                                className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold gap-2 h-12 px-8"
                                                            >
                                                                {isReviewingWithPlaybook ? (
                                                                    <><Loader2 className="h-4 w-4 animate-spin" /> Revisando...</>
                                                                ) : (
                                                                    <><Sparkles className="h-4 w-4" /> Ejecutar Revisión con Playbook</>
                                                                )}
                                                            </Button>
                                                        </div>
                                                    )}
                                                </div>
                                            )}


                                            {result.summary_pdf_url && (
                                                <div className="mt-8 flex justify-end gap-3">
                                                    <Button
                                                        onClick={() => openDocument(result.summary_pdf_url!, "Informe Técnico")}
                                                        className="bg-slate-900 text-white gap-2 font-bold px-6 h-11 rounded-xl"
                                                    >
                                                        <FileDown className="h-4 w-4" /> Descargar Informe PDF
                                                    </Button>
                                                </div>
                                            )}
                                            </div>
                                        </Panel>
                                    )}

                                    {!isMobile && (
                                        <PanelResizeHandle className="crv2-resize-handle">
                                            <div className="crv2-handle-bar" />
                                        </PanelResizeHandle>
                                    )}

                                    {/* Panel Derecho: Editor Permanente */}
                                    {(!isMobile || mobileView === "editor") && (
                                        <Panel minSize={isMobile ? 100 : 30}>
                                            <div className="crv2-right-panel">
                                            <div className="p-4 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-8 h-8 rounded-lg bg-slate-900 flex items-center justify-center text-white">
                                                        <FileText className="h-4 w-4" />
                                                    </div>
                                                    <div>
                                                        <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">Editor de Redlines</h3>
                                                        <p className="text-[10px] text-slate-500">Edición en vivo del contrato original</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {isFetchingContent && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="h-8 px-3 text-[10px] font-bold border-blue-200 text-blue-700 hover:bg-blue-50 rounded-lg gap-2"
                                                        onClick={() => {
                                                            setSyncToken(Date.now());
                                                            toast.success("Sincronizando sugerencias de IA...");
                                                        }}
                                                    >
                                                        <RefreshCw className="h-3.5 w-3.5" /> SINCRONIZAR
                                                    </Button>
                                                </div>
                                            </div>

                                            <div className="editor-wrapper">
                                                {redlinedContent ? (
                                                    <LegalEditor
                                                        documentId={currentDocId || "temp-doc"}
                                                        initialContent={redlinedContent}
                                                        syncToken={syncToken}
                                                        userName={user?.email?.split('@')[0] || "Abogado"}
                                                        userId={user?.id}
                                                        onSave={(content) => {
                                                            toast.success("Documento guardado localmente");
                                                        }}
                                                    />
                                                ) : fetchError ? (
                                                    <div className="flex flex-col items-center justify-center p-12 text-center h-full">
                                                        <AlertCircle className="h-8 w-8 text-red-500 mb-2" />
                                                        <p className="text-slate-500 text-xs mb-4">{fetchError}</p>
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => {
                                                                const docId = currentDocId || result?.summary_pdf_url?.match(/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{12})/i)?.[0];
                                                                if (docId) fetchInitialContent(docId);
                                                            }}
                                                        >
                                                            Reintentar
                                                        </Button>
                                                    </div>
                                                ) : (
                                                    <div className="flex flex-col items-center justify-center p-12 text-center h-full">
                                                        <Loader2 className="h-8 w-8 text-slate-200 animate-spin mb-3" />
                                                        <p className="text-slate-400 text-[10px] italic">Cargando texto original...</p>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="p-3 bg-blue-50/50 border-t border-blue-100">
                                                <p className="text-[10px] text-blue-700 leading-relaxed text-center">
                                                    <b>Modo de Redacción en Vivo:</b> Los cambios sugeridos por la IA se han pre-aplicado.
                                                    Usa <span className="text-red-600 font-bold">rojo</span> para eliminaciones y <span className="text-green-600 font-bold">verde</span> para adiciones.
                                                </p>
                                            </div>
                                        </div>
                                        </Panel>
                                    )}
                                </PanelGroup>
                            </div>
                        </div>
                    )}
                </div>
                )}
            </div>


            {/* Proceso Selection Dialog */}
            <Dialog open={showProcesoDialog} onOpenChange={setShowProcesoDialog}>
                <DialogContent className="sm:max-w-[500px] max-h-[80vh] flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Gavel className="h-5 w-5 text-blue-600" />
                            Traer Expediente Judicial
                        </DialogTitle>
                        <DialogDescription className="sr-only">
                            Selecciona un expediente judicial de tus procesos para traer un documento.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4 flex-1 overflow-y-auto">
                        <p className="text-sm text-slate-500 mb-4">
                            Selecciona el proceso del cual deseas traer un documento para analizarlo como contrato.
                        </p>
                        {loadingProcesos ? (
                            <div className="flex flex-col items-center justify-center py-8 gap-3">
                                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                                <span className="text-sm text-slate-400">Cargando tus procesos...</span>
                            </div>
                        ) : procesos.length === 0 ? (
                            <div className="text-center py-8 border-2 border-dashed rounded-xl">
                                <FolderOpen className="h-10 w-10 text-slate-200 mx-auto mb-2" />
                                <p className="text-sm text-slate-400 font-medium">No tienes procesos guardados</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {procesos.map((p) => (
                                    <div
                                        key={p.numero_proceso}
                                        onClick={() => handleSelectProceso(p)}
                                        className="group p-4 rounded-xl border border-slate-200 bg-white transition-all cursor-pointer flex flex-col gap-1 hover:border-blue-400 hover:bg-blue-50/50 hover:shadow-sm"
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="text-[11px] font-bold text-blue-600 bg-blue-100/50 px-2 py-0.5 rounded uppercase group-hover:bg-blue-600 group-hover:text-white transition-colors">
                                                {p.numero_proceso}
                                            </span>
                                            {p.metadata?.client_name && (
                                                <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">
                                                    {p.metadata.client_name}
                                                </span>
                                            )}
                                        </div>
                                        <span className="text-sm font-semibold text-slate-700 line-clamp-1 group-hover:text-blue-700 transition-colors">
                                            {p.asunto || "Sin asunto"}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <DialogFooter className="border-t pt-4">
                        <Button variant="outline" onClick={() => setShowProcesoDialog(false)}>Cancelar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* File Selection Dialog */}
            <Dialog open={showFileDialog} onOpenChange={setShowFileDialog}>
                <DialogContent className="sm:max-w-[500px] max-h-[80vh] flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FileCheck className="h-5 w-5 text-blue-600" />
                            Seleccionar Documento del Expediente
                        </DialogTitle>
                        <DialogDescription className="sr-only">
                            Selecciona el archivo específico del expediente que deseas analizar como contrato.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4 flex-1 overflow-y-auto">
                        {loadingFiles ? (
                            <div className="flex flex-col items-center justify-center py-8 gap-3">
                                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                                <span className="text-sm text-slate-400">Cargando archivos del proceso...</span>
                            </div>
                        ) : procesoFiles.length === 0 ? (
                            <div className="text-center py-8 border-2 border-dashed rounded-xl">
                                <FolderOpen className="h-10 w-10 text-slate-200 mx-auto mb-2" />
                                <p className="text-sm text-slate-400 font-medium">Este proceso no tiene archivos adjuntos</p>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {procesoFiles.map((file) => (
                                    <div
                                        key={file.id}
                                        onClick={() => handleSelectDespachoFile(file)}
                                        className="p-4 rounded-xl border border-slate-200 bg-white transition-all cursor-pointer flex items-center gap-3 hover:border-blue-400 hover:bg-blue-50/50"
                                    >
                                        <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                                            <FileText className="h-5 w-5 text-blue-600" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-slate-700 truncate">{file.file_name}</p>
                                            <p className="text-xs text-slate-500">{(file.file_size / 1024).toFixed(1)} KB • {new Date(file.created_at).toLocaleDateString()}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <DialogFooter className="border-t pt-4">
                        <Button variant="outline" onClick={() => { setShowFileDialog(false); setShowProcesoDialog(true); }}>Atrás</Button>
                        <Button variant="ghost" onClick={() => setShowFileDialog(false)}>Cerrar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Start Despacho Analysis if selected */}
            {selectedDespachoFile && isDespachoAnalysis && step === 2 && (
                <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 animate-in fade-in slide-in-from-bottom-4 duration-300">
                    <Button
                        onClick={startDespachoAnalysis}
                        className="bg-blue-600 hover:bg-blue-700 text-white shadow-xl px-10 py-6 rounded-full font-bold text-lg border-4 border-white"
                        disabled={isUploading}
                    >
                        {isUploading ? (
                            <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Analizando...</>
                        ) : (
                            <>Analizar Documento de Despacho</>
                        )}
                    </Button>
                </div>
            )}
        </div>
    );
}
